import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import {
  SOTERIA_COLUMNS,
  resolveSemanticMatchColumnIndex,
  soteriaColumnIndex,
} from '../constants/soteriaDataTapeColumns';
import {
  formatSourceValidationRowError,
  humanizeValidationExpected,
  validateFieldValue,
} from '../utils/validationMessages';
import './ContainerExcelBuilder.css';

// String similarity function (Dice coefficient / Sørensen–Dice coefficient)
function stringSimilarity(str1: string, str2: string): number {
  const s1 = str1.toLowerCase().trim();
  const s2 = str2.toLowerCase().trim();
  
  if (s1 === s2) return 1.0;
  if (s1.length === 0 || s2.length === 0) return 0.0;
  
  // Create bigrams (pairs of adjacent characters)
  const getBigrams = (str: string): Set<string> => {
    const bigrams = new Set<string>();
    for (let i = 0; i < str.length - 1; i++) {
      bigrams.add(str.substring(i, i + 2));
    }
    return bigrams;
  };
  
  const bigrams1 = getBigrams(s1);
  const bigrams2 = getBigrams(s2);
  
  // Calculate intersection
  let intersection = 0;
  bigrams1.forEach((bigram) => {
    if (bigrams2.has(bigram)) {
      intersection++;
    }
  });
  
  // Dice coefficient: 2 * |A ∩ B| / (|A| + |B|)
  return (2 * intersection) / (bigrams1.size + bigrams2.size);
}

// Date fields that should use a date picker
const DATE_FIELDS = new Set(['Project Due Date', 'Appraisal Date', 'Date of Last Payment']);

/** Client wait for POST /data-tape/match-columns. Default server timeout is 180s (gpt-5.4 Responses); 30s caused AbortError → fuzzy fallback with different pairings than the API. */
const MATCH_COLUMNS_CLIENT_TIMEOUT_MS = 300000;

// Allowed values for restricted Soteria fields
const RESTRICTED_FIELD_VALUES: Record<string, string[]> = {
  'Industry': [
    'Retail',
    'Capital Goods',
    'Services',
    'Healthcare',
    'Energy',
    'Consumer Products',
    'Utilities',
    'Technology',
    'Basic Materials',
    'Transportation',
    'Real Estate',
    'Aviation'
  ],
  'Type of Sale': [
    'Loan',
    'Equity',
    'Debt+Equity',
    'Land Contract',
    'Lease Options',
    'Open (Debt/Equity)',
    'Assets',
    'IP'
  ],
  'State of Incorporation': [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
    'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
    'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri',
    'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina',
    'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
    'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
  ],
  'Region': [
    'Any',
    'East Coast',
    'West Coast',
    'Midwest',
    'Northeast',
    'South',
    'Nationwide'
  ],
  'Type of Security': [
    'Real Estate',
    'Plant/Equipment',
    'Inventory',
    'Cash',
    'Unpaid Invoices',
    'Blanket Liens',
    'Natural Reserves',
    'Marketable Securities',
    'No Security'
  ],
  'Type of Borrower': [
    'CORPORATION',
    'INDIVIDUAL'
  ],
  'Type': [
    'CORPORATION',
    'INDIVIDUAL'
  ],
  'Interest Base':[
      "1MT",
      "3MT",
      "1YT",
      "2YT",
      "3YT",
      "5YT",
      "10YT",
      "10YT Inflation-Indexed Security Rate",
      "10-2 Year Treasury Yield Spread",
      "30YT",
      "30 Year Mortgage Rate",
      "30 YT Inflation-Indexed Security Rate",
      "30-10 Year Treasury Yield Spread",
      "Primary Credit Rate",
      "Target Federal Funds Rate Lower Limit",
      "US Bank Prime Loan Rate",
      "US Corporate A Effective Yield",
      "US Corporate AA Effective Yield",
      "US Corporate AAA Effective Yield",
      "US Corporate BBB Bond Risk Premium",
      "US Corporate BBB Effective Yield",
      "US High Yield B Effective Yield",
      "US High Yield BB Effective Yield",
      "US High Yield CCC Effective Yield",
      "US Real Interest Rate (NSA)",
      "3 Month LIBOR",
      "Other/Fixed",
      "WSJ Prime Rate" 
  ],
  'Payments Default': [
    'Yes',
    'No',
    'NA'
  ],
  'Covenants Default': [
    'Yes',
    'No',
    'NA'
  ],
  'Service of the Loans after the Sale': [
    'Seller',
    'Buyer',
    'TBD'
  ],
  'Secured': [
    'Yes',
    'No',
    'NA'
  ],
  'Syndicated': [
    'Yes',
    'No',
    'NA'
  ],
  'Level of Security':[
    'First Lien',
    'Second Lien',
    'Third Lien',
    'Other (please check loan documents)',
  ],
  'Guaranty': [
    'Yes',
    'No',
  ],
  'Recourse':[
    'Yes',
    'No',
    'NA'
  ],
  'Relationship':[
    'Owner', 
    'Affiliate', 
    'Third Party'
  ],  
  'Property type': [
    'Commercial',
    'Industrial',
    '1-4 Family',
    'Multifamily',
    'Construction',
    'Hospitality',
    'Residential',
  ],
  'Asset Secures Loan Below': [
    'Yes',
    'No',
  ],
};

const REAL_ESTATE_SECTORS = [
  'Hospitality',
  'Residential',
  'Commercial',
  'Entrepreneurship',
  'Construction',
  'Mostly Hospitality',
  'Mostly Residential',
  'Mostly Commercial',
  'Mostly Entrepreneurship',
  'Mostly Construction',
  'Mixed',
];

// Soteria Data Tape column titles — see ../constants/soteriaDataTapeColumns.ts

interface UploadResponse {
  success: boolean;
  message: string;
  headers?: string[];
  emptyHeaders?: string[];
}

function stripEmptyHeaderMappings(
  mappingRecord: Record<string, string>,
  emptyHeaderSet: ReadonlySet<string>
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(mappingRecord).filter(([, header]) => !emptyHeaderSet.has(header))
  );
}

/** Drop copy links that point at a removed/unmapped source column. */
function withoutCopiedMappingReferences(
  copied: Record<string, string>,
  columnKey: string
): Record<string, string> {
  const next = { ...copied };
  delete next[columnKey];
  for (const [target, source] of Object.entries(next)) {
    if (source === columnKey) {
      delete next[target];
    }
  }
  return next;
}

interface ErrorResponse {
  error: string;
  code?: string;
  details?: unknown;
  sourceValidation?: SourceValidationResponse;
}

interface SourceValidationError {
  columnIndex: number;
  field: string;
  uploadedHeader: string;
  rowNumber: number;
  value: string;
  expected: string;
  message: string;
  normalizedValue?: string | number;
}

interface SourceValidationResponse {
  isValid: boolean;
  errorsByColumn: Record<string, SourceValidationError[]>;
  summary: {
    totalErrors: number;
    fieldsWithErrors: number;
  };
}

interface SourceConflictSuggestion {
  source_value: string;
  suggested_value: string | null;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW' | 'NONE';
  reasoning: string;
}

interface SourceOverrideValidationRule {
  kind: 'TEXT' | 'NUMERIC' | 'DECIMAL' | 'DATE' | 'URL';
  nullable: boolean;
  allowedValues: string[];
  integer: boolean;
  year: boolean;
  min?: number;
  max?: number;
}

interface SourceConflictResolution {
  columnIndex: number;
  field: string;
  uploadedHeader: string;
  allowedValues: string[];
  expected: string;
  validationRule?: SourceOverrideValidationRule;
  suggestions: SourceConflictSuggestion[];
}

type ResolvedSourceValues = Record<string, Record<string, string>>;

interface MatchResult {
  address: string;
  folderUrl: string | null;
  folderName: string | null;
  similarity: number;
}

interface BoxAiColumnResult {
  address: string;
  value: string | null;
  fileId: string | null;
  fileName: string | null;
  fileUrl: string | null;
  folderName: string | null;
  error: string | null;
}

interface BoxAiMultiColumnResult {
  columnIndex: number;
  columnName: string;
  results: BoxAiColumnResult[];
}

interface SourceDefaultInferenceSuggestion {
  columnIndex: number;
  field: string;
  value: string;
  reason: string;
}

interface SourceAutomationReadiness {
  columnIndex: number;
  field: string;
  mode: 'autoCalculate' | 'autoGenerate';
  isReady: boolean;
  missingFields: string[];
  reason: string;
  dependencyChecks?: Array<{
    field: string;
    mappedIndex: number;
    uploadHeader: string | null;
    rowsChecked: number;
    rowsWithValue: number;
    status: 'ok' | 'unmapped' | 'incomplete';
  }>;
}

function buildFieldMappings(mappings: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (let i = 0; i < SOTERIA_COLUMNS.length; i++) {
    const header = mappings[String(i)]?.trim();
    if (header) out[SOTERIA_COLUMNS[i]!.trim()] = header;
  }
  for (const [key, header] of Object.entries(mappings)) {
    if (!header?.trim() || /^\d+$/.test(key)) continue;
    const idx = soteriaColumnIndex(key);
    if (idx >= 0) out[SOTERIA_COLUMNS[idx]!.trim()] = header.trim();
  }
  return out;
}

/** Prefer field name over API column_index — indices drift when the schema changes. */
function resolveAutomationReadinessColumnIndex(readiness: SourceAutomationReadiness): number {
  const fromField = soteriaColumnIndex(readiness.field);
  if (fromField >= 0) return fromField;
  return readiness.columnIndex;
}

function normalizeAutomationReadiness(
  readiness: SourceAutomationReadiness
): SourceAutomationReadiness {
  const columnIndex = resolveAutomationReadinessColumnIndex(readiness);
  return {
    ...readiness,
    columnIndex,
    field: SOTERIA_COLUMNS[columnIndex]?.trim() ?? readiness.field.trim(),
  };
}

function buildAutomationReadinessMap(
  items: SourceAutomationReadiness[]
): Record<string, SourceAutomationReadiness> {
  return Object.fromEntries(
    items.map((item) => {
      const normalized = normalizeAutomationReadiness(item);
      return [String(normalized.columnIndex), normalized];
    })
  );
}

function automationReadinessForColumn(
  readinessMap: Record<string, SourceAutomationReadiness>,
  columnKey: string,
  soteriaCol: string
): SourceAutomationReadiness | undefined {
  const expected = soteriaCol.trim();
  const byField = Object.values(readinessMap).find(
    (item) => item.field.trim() === expected
  );
  if (byField) return byField;
  const byKey = readinessMap[columnKey];
  return byKey?.field.trim() === expected ? byKey : undefined;
}

type ParameterSource = 'soteria' | 'original';
const ORIGINAL_PARAM_PREFIX = 'orig:';
const EXECUTIVE_SUMMARY_FIELD = 'Executive Summary';
const TEASER_FIELD = 'Teaser';
const AUTO_GENERATE_FIELDS = new Set([EXECUTIVE_SUMMARY_FIELD, TEASER_FIELD]);

/** Fields that support server-side Auto Calculate during generate (must match backend AUTO_CALCULATE_FIELDS). */
const AUTO_CALCULATE_FIELDS = new Set<string>([
  'Monthly Loan Payment',
  'Current Interest Rate',
  'LTV',
  'Cap Rate',
  'DSCR @Current Interest Rate',
  'GRM',
  'Debt Yield',
  'Region',
  'Appraisal Value - Real Estate',
  'Appraisal Value Total',
]);

const DATA_ROOM_LINK_COL_INDEX = SOTERIA_COLUMNS.indexOf('Data Room Link');
const INDUSTRY_COL_INDEX = SOTERIA_COLUMNS.indexOf('Industry');
const SECTOR_COL_INDEX = SOTERIA_COLUMNS.indexOf('Sector');

/** Soteria columns that should never be auto-completed from the data room via Box AI. */
const COMPLETE_FROM_DATA_ROOM_EXCLUDED = new Set<number>(
  [
    'Project Identifier',
    'Team (in the bank/seller)',
    'Loan Identification',
    'Loan Identification ',
    'Data Room Link',
  ]
    .map((name) => SOTERIA_COLUMNS.indexOf(name))
    .filter((idx) => idx >= 0)
);

const AUTO_CALC_COL_INDICES = new Set<number>();
for (let i = 0; i < SOTERIA_COLUMNS.length; i++) {
  const t = SOTERIA_COLUMNS[i];
  if (t && AUTO_CALCULATE_FIELDS.has(t)) {
    AUTO_CALC_COL_INDICES.add(i);
  }
}

const AUTO_GENERATE_COL_INDICES = new Set<number>();
for (let i = 0; i < SOTERIA_COLUMNS.length; i++) {
  const t = SOTERIA_COLUMNS[i];
  if (t && AUTO_GENERATE_FIELDS.has(t)) {
    AUTO_GENERATE_COL_INDICES.add(i);
  }
}

const SOTERIA_COLUMN_GROUPS = [
  { title: 'Project Details', firstField: 'Project Identifier' },
  { title: 'General', firstField: 'Borrower Name' },
  { title: 'Loan Details', firstField: 'Loan Identification ' },
  { title: 'Other Terms/Main Covenants', firstField: 'Term/Covenant 1' },
  { title: 'Guarantor Details (If Guaranteed)', firstField: 'Full Legal Name' },
  { title: 'Real Estate Details', firstField: 'Property Identifier' },
  { title: 'Pricing Guidance', firstField: 'Total Outstanding Balance of Loans in Priority Over the Seller' },
]
  .map((group) => ({
    title: group.title,
    startIndex: SOTERIA_COLUMNS.indexOf(group.firstField),
  }))
  .filter((group) => group.startIndex >= 0);

const SOTERIA_COLUMN_GROUP_BY_INDEX = new Map(
  SOTERIA_COLUMN_GROUPS.map((group) => [group.startIndex, group.title] as const)
);

type AutoBuildStepStatus = 'pending' | 'active' | 'done' | 'error' | 'skipped';
interface AutoBuildStep {
  id: string;
  label: string;
  status: AutoBuildStepStatus;
  detail?: string;
}
const AUTO_BUILD_STEP_DEFS: { id: string; label: string }[] = [
  { id: 'extract', label: 'Extract headers from the uploaded file' },
  { id: 'map', label: 'Map columns to the Soteria Data Tape' },
  { id: 'resolve', label: 'Resolve value conflicts' },
  { id: 'autofill', label: 'Auto-fill columns deducible from other fields' },
  { id: 'automation', label: 'Set auto-calculated / auto-generated fields' },
  { id: 'boxai', label: 'Collect data from the Data Room with Box AI' },
  { id: 'generate', label: 'Generate the Soteria Data Tape' },
  { id: 'download', label: 'Download the Soteria Data Tape' },
];

type ContainerExcelBuilderMode = 'full' | 'box-dataroom';

interface ContainerExcelBuilderProps {
  mode?: ContainerExcelBuilderMode;
}

function ContainerExcelBuilder({ mode = 'full' }: ContainerExcelBuilderProps) {
  const isBoxDataroomMode = mode === 'box-dataroom';
  const [file, setFile] = useState<File | null>(null);
  const [uploadedHeaders, setUploadedHeaders] = useState<string[]>([]);
  /** Uploaded column headers with no values in any data row — excluded from auto-matching. */
  const [emptyHeaders, setEmptyHeaders] = useState<Set<string>>(new Set());
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [draggedHeader, setDraggedHeader] = useState<string | null>(null);
  const [dragOverSoteria, setDragOverSoteria] = useState<string | null>(null);
  const [removedHeaders, setRemovedHeaders] = useState<Set<string>>(new Set());
  const [soteriaSortOrder, setSoteriaSortOrder] = useState<'original' | 'a-z' | 'z-a'>('original');
  const [chipsSortOrder, setChipsSortOrder] = useState<'original' | 'a-z' | 'z-a'>('original');
  const [isGenerating, setIsGenerating] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [hasGeneratedOnce, setHasGeneratedOnce] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [isPairing, setIsPairing] = useState(false);
  const [previewDialog, setPreviewDialog] = useState<{ open: boolean; column: string | null; values: string[] }>({
    open: false,
    column: null,
    values: [],
  });
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [fixedValues, setFixedValues] = useState<Record<string, string>>({});
  const [autoGenerateFields, setAutoGenerateFields] = useState<Record<string, true>>({});
  const [autoCalculateFields, setAutoCalculateFields] = useState<Record<string, true>>({});
  /** User removed auto-calculate/auto-generate — do not re-apply on later mapping changes. */
  const [declinedAutoCalculateFields, setDeclinedAutoCalculateFields] = useState<Record<string, true>>({});
  const [declinedAutoGenerateFields, setDeclinedAutoGenerateFields] = useState<Record<string, true>>({});
  const [sourceAutomationReadiness, setSourceAutomationReadiness] = useState<Record<string, SourceAutomationReadiness>>({});
  const [editingFixedValue, setEditingFixedValue] = useState<string | null>(null);
  const [copyDialog, setCopyDialog] = useState<{ open: boolean; targetColumnKey: string | null }>({
    open: false,
    targetColumnKey: null,
  });
  /** Keys are 0-based Soteria column indices as strings; values are source column index strings when copying. */
  const [copiedMappings, setCopiedMappings] = useState<Record<string, string>>({});
  const [sourceValidationErrors, setSourceValidationErrors] = useState<Record<string, SourceValidationError[]>>({});
  const [resolvedSourceValues, setResolvedSourceValues] = useState<ResolvedSourceValues>({});
  const [isValidatingSource, setIsValidatingSource] = useState(false);
  const [sourceValidationError, setSourceValidationError] = useState<string | null>(null);
  const [isResolvingSourceConflict, setIsResolvingSourceConflict] = useState(false);
  const [resolvingConflictColumnKey, setResolvingConflictColumnKey] = useState<string | null>(null);
  const [sourceConflictDialog, setSourceConflictDialog] = useState<{
    open: boolean;
    columnKey: string | null;
    result: SourceConflictResolution | null;
    error: string | null;
  }>({
    open: false,
    columnKey: null,
    result: null,
    error: null,
  });
  const [sourceConflictOverrides, setSourceConflictOverrides] = useState<Record<string, string>>({});
  const [sourceConflictOverrideErrors, setSourceConflictOverrideErrors] = useState<Record<string, string>>({});
  const [resolvedSourceDialog, setResolvedSourceDialog] = useState<{
    open: boolean;
    columnKey: string | null;
  }>({
    open: false,
    columnKey: null,
  });
  const [dataRoomLinkDialog, setDataRoomLinkDialog] = useState<{ open: boolean }>({ open: false });
  const [boxFolderUrl, setBoxFolderUrl] = useState<string>('');
  const [isMatching, setIsMatching] = useState(false);
  const [matchResults, setMatchResults] = useState<{
    matches: Array<{ address: string; folderUrl: string | null; folderName: string | null; similarity: number }>;
    totalAddresses: number;
    matchedCount: number;
  } | null>(null);
  const [matchError, setMatchError] = useState<string | null>(null);
  const [savedDataRoomLink, setSavedDataRoomLink] = useState<{
    boxFolderUrl: string;
    matches: Array<{ address: string; folderUrl: string | null; folderName: string | null; similarity: number }>;
  } | null>(null);
  const [pendingBoxAiColumnKey, setPendingBoxAiColumnKey] = useState<string | null>(null);
  const [boxAiFields, setBoxAiFields] = useState<Record<string, true>>({});
  // Box AI base values per column, keyed by address: { columnKey: { address: value } }.
  // Corrections live separately in resolvedSourceValues (value-keyed), so the existing
  // resolve-conflicts flow applies on top of these base values.
  const [boxAiValues, setBoxAiValues] = useState<Record<string, Record<string, string>>>({});
  // Per-column Box AI results (address, value, source file) kept so the chip's eye icon
  // can replay where each value came from.
  const [boxAiResults, setBoxAiResults] = useState<Record<string, BoxAiColumnResult[]>>({});
  const [boxAiPreviewDialog, setBoxAiPreviewDialog] = useState<{
    open: boolean;
    columnKey: string | null;
    columnName: string;
  }>({ open: false, columnKey: null, columnName: '' });
  // ── Automated "Upload & Generate Datatape" orchestration ──
  const [isAutoBuilding, setIsAutoBuilding] = useState(false);
  const [autoBuild, setAutoBuild] = useState<{
    phase: 'idle' | 'collect-url' | 'running' | 'resolve-conflicts' | 'done' | 'error';
    steps: AutoBuildStep[];
    error: string | null;
    downloadUrl: string | null;
  }>({ phase: 'idle', steps: [], error: null, downloadUrl: null });
  const [autoBuildUrlInput, setAutoBuildUrlInput] = useState('');
  const [autoBuildConflicts, setAutoBuildConflicts] = useState<SourceConflictResolution[]>([]);
  const [autoBuildConflictInputs, setAutoBuildConflictInputs] = useState<
    Record<string, Record<string, string>>
  >({});
  const [autoBuildConflictErrors, setAutoBuildConflictErrors] = useState<
    Record<string, Record<string, string>>
  >({});
  const autoBuildConflictResolverRef = useRef<
    ((resolved: Record<string, Record<string, string>>) => void) | null
  >(null);
  const autoBuildAbortRef = useRef(false);
  // Columns already attempted for HIGH-confidence auto-resolution in the manual flow.
  const autoResolveAttemptedRef = useRef<Set<string>>(new Set());
  const [boxAiDialog, setBoxAiDialog] = useState<{
    open: boolean;
    columnKey: string | null;
    columnName: string;
    results: BoxAiColumnResult[];
    error: string | null;
  }>({
    open: false,
    columnKey: null,
    columnName: '',
    results: [],
    error: null,
  });
  const [isBoxAiLoading, setIsBoxAiLoading] = useState(false);
  const [completeConfirmOpen, setCompleteConfirmOpen] = useState(false);
  const [pendingCompleteFromDataRoom, setPendingCompleteFromDataRoom] = useState(false);
  const [isCompleteLoading, setIsCompleteLoading] = useState(false);
  const [completeDialog, setCompleteDialog] = useState<{
    open: boolean;
    columns: BoxAiMultiColumnResult[];
    activeColumnIndex: number | null;
    excludedColumnKeys: string[];
    error: string | null;
  }>({
    open: false,
    columns: [],
    activeColumnIndex: null,
    excludedColumnKeys: [],
    error: null,
  });
  const [parameterAutocomplete, setParameterAutocomplete] = useState<{
    open: boolean;
    inputValue: string;
    cursorPosition: number;
    filterText: string;
    source: ParameterSource;
    targetColumnKey: string | null;
  }>({
    open: false,
    inputValue: '',
    cursorPosition: 0,
    filterText: '',
    source: 'soteria',
    targetColumnKey: null,
  });
  
  // Ref to store input elements for cursor positioning
  const inputRefs = useRef<Record<string, HTMLInputElement>>({});
  // Ref for preview elements (for scroll sync)
  const previewRefs = useRef<Record<string, HTMLDivElement>>({});

  const sourceValidationColumnErrorCount = useMemo(
    () => Object.values(sourceValidationErrors).filter((errors) => errors.length > 0).length,
    [sourceValidationErrors]
  );

  const resolvedSourceColumnCount = useMemo(
    () => Object.values(resolvedSourceValues).filter((values) => Object.keys(values).length > 0).length,
    [resolvedSourceValues]
  );

  const resolvedSourceDialogDetails = useMemo(() => {
    const columnKey = resolvedSourceDialog.columnKey;
    if (!columnKey) return null;
    const columnIndex = Number.parseInt(columnKey, 10);
    const sourceColumnKey = copiedMappings[columnKey] || columnKey;
    return {
      columnKey,
      field: SOTERIA_COLUMNS[columnIndex] || `Column ${columnKey}`,
      uploadedHeader: mappings[sourceColumnKey] || null,
      entries: Object.entries(resolvedSourceValues[columnKey] || {}),
    };
  }, [copiedMappings, mappings, resolvedSourceDialog.columnKey, resolvedSourceValues]);

  const sourceConflictApplyableCount = useMemo(() => {
    const result = sourceConflictDialog.result;
    if (!result) return 0;
    return result.suggestions.filter((suggestion) => {
      const override = sourceConflictOverrides[suggestion.source_value]?.trim();
      const suggested = suggestion.suggested_value?.trim();
      return Boolean((override || suggested) && !sourceConflictOverrideErrors[suggestion.source_value]);
    }).length;
  }, [sourceConflictDialog.result, sourceConflictOverrides, sourceConflictOverrideErrors]);
  
  // Track pending cursor positions after parameter insertion
  const pendingCursorRef = useRef<{ col: string; pos: number } | null>(null);
  
  // Effect to set cursor position after React updates the DOM
  useEffect(() => {
    const pending = pendingCursorRef.current;
    if (!pending) return;
    
    const input = inputRefs.current[pending.col];
    if (input && input.value === (fixedValues[pending.col] || '')) {
      input.setSelectionRange(pending.pos, pending.pos);
      input.focus();
      pendingCursorRef.current = null;
    }
  }, [fixedValues]);
  
  // Sync preview scroll with input scroll
  const syncPreviewScroll = (columnKey: string) => {
    const input = inputRefs.current[columnKey];
    const preview = previewRefs.current[columnKey];
    if (input && preview) {
      preview.scrollLeft = input.scrollLeft;
    }
  };

  useEffect(() => {
    if (isAutoBuilding) return;
    const hasMappings = Object.keys(mappings).length > 0;
    const hasBoxAi = Object.keys(boxAiFields).length > 0;
    if (!file || uploadedHeaders.length === 0 || (!hasMappings && !hasBoxAi)) {
      setSourceValidationErrors({});
      setSourceValidationError(null);
      setIsValidatingSource(false);
      return;
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(async () => {
      setIsValidatingSource(true);
      setSourceValidationError(null);

      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('mappings', JSON.stringify(mappings));
        formData.append('copiedMappings', JSON.stringify(copiedMappings));
        formData.append('resolvedSourceValues', JSON.stringify(resolvedSourceValues));
        formData.append('autoGenerateFields', JSON.stringify(Object.keys(autoGenerateFields)));
        formData.append('autoCalculateFields', JSON.stringify(Object.keys(autoCalculateFields)));
        formData.append('boxAiFields', JSON.stringify(Object.keys(boxAiFields)));
        formData.append('boxAiValues', JSON.stringify(boxAiValues));

        const response = await fetch(`${API_BASE_URL}/data-tape/validate-source-mappings`, {
          method: 'POST',
          body: formData,
          signal: controller.signal,
        });
        const data = await response.json();

        if (controller.signal.aborted) return;

        if (!response.ok || data.success !== true || !data.sourceValidation) {
          throw new Error(data.error || 'Failed to validate imported values.');
        }

        const validation = data.sourceValidation as SourceValidationResponse;
        setSourceValidationErrors(validation.errorsByColumn || {});
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (controller.signal.aborted) return;
        setSourceValidationError(err instanceof Error ? err.message : 'Failed to validate imported values.');
      } finally {
        if (!controller.signal.aborted) {
          setIsValidatingSource(false);
        }
      }
    }, 350);

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [file, uploadedHeaders.length, mappings, copiedMappings, resolvedSourceValues, autoGenerateFields, autoCalculateFields, boxAiFields, boxAiValues, isAutoBuilding]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setUploadedHeaders([]);
      setEmptyHeaders(new Set());
      setMappings({});
      setRemovedHeaders(new Set());
      setSoteriaSortOrder('original');
      setChipsSortOrder('original');
      setError(null);
      setUploadSuccess(false);
      setDownloadUrl(null);
      setGenerateError(null);
      setFixedValues({});
      setAutoGenerateFields({});
      setAutoCalculateFields({});
      setDeclinedAutoCalculateFields({});
      setDeclinedAutoGenerateFields({});
      setSourceAutomationReadiness({});
      setEditingFixedValue(null);
      setCopiedMappings({});
      setResolvedSourceValues({});
      setSourceValidationErrors({});
      setSourceValidationError(null);
      setIsValidatingSource(false);
      setSavedDataRoomLink(null);
      setBoxFolderUrl('');
      setMatchResults(null);
      setPendingBoxAiColumnKey(null);
      setBoxAiFields({});
      setBoxAiValues({});
      setBoxAiResults({});
      setBoxAiPreviewDialog({ open: false, columnKey: null, columnName: '' });
      setBoxAiDialog({
        open: false,
        columnKey: null,
        columnName: '',
        results: [],
        error: null,
      });
      setIsBoxAiLoading(false);
      setCompleteConfirmOpen(false);
      setPendingCompleteFromDataRoom(false);
      setIsCompleteLoading(false);
      setCompleteDialog({
        open: false,
        columns: [],
        activeColumnIndex: null,
        excludedColumnKeys: [],
        error: null,
      });
      autoBuildAbortRef.current = true;
      autoBuildConflictResolverRef.current = null;
      setIsAutoBuilding(false);
      setAutoBuild({ phase: 'idle', steps: [], error: null, downloadUrl: null });
      setAutoBuildConflicts([]);
      setAutoBuildConflictInputs({});
      setAutoBuildConflictErrors({});
    }
  };

  const applyInferredSourceDefaults = useCallback(async (
    fileForInference: File,
    currentMappings: Record<string, string>,
    applyAutomation = false
  ) => {
    try {
      const fieldMappings = buildFieldMappings(currentMappings);
      const formData = new FormData();
      formData.append('file', fileForInference);
      formData.append('mappings', JSON.stringify(currentMappings));
      formData.append('fieldMappings', JSON.stringify(fieldMappings));
      formData.append('copiedMappings', JSON.stringify(copiedMappings));
      formData.append('fixedValues', JSON.stringify(fixedValues));
      formData.append('resolvedSourceValues', JSON.stringify(resolvedSourceValues));
      formData.append('autoCalculateFields', JSON.stringify(Object.keys(autoCalculateFields)));

      const response = await fetch(`${API_BASE_URL}/data-tape/infer-source-defaults`, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();

      if (!response.ok || data.success !== true || !Array.isArray(data.suggestions)) {
        return;
      }

      const suggestions = data.suggestions as SourceDefaultInferenceSuggestion[];
      const automationReadiness = Array.isArray(data.automationReadiness)
        ? data.automationReadiness as SourceAutomationReadiness[]
        : [];

      setSourceAutomationReadiness(
        buildAutomationReadinessMap(automationReadiness)
      );

      setFixedValues((prev) => {
        const next = { ...prev };
        let changed = false;
        for (const suggestion of suggestions) {
          const columnKey = String(suggestion.columnIndex);
          if (!currentMappings[columnKey] && !next[columnKey] && !boxAiFields[columnKey]) {
            next[columnKey] = suggestion.value;
            changed = true;
          }
        }
        return changed ? next : prev;
      });

      if (applyAutomation) {
        setAutoGenerateFields((prev) => {
          const next = { ...prev };
          let changed = false;
          for (const readiness of automationReadiness) {
            const normalized = normalizeAutomationReadiness(readiness);
            const columnKey = String(normalized.columnIndex);
            if (
              readiness.mode === 'autoGenerate' &&
              readiness.isReady &&
              !currentMappings[columnKey] &&
              !fixedValues[columnKey] &&
              !boxAiFields[columnKey] &&
              !next[columnKey] &&
              !declinedAutoGenerateFields[columnKey]
            ) {
              next[columnKey] = true;
              changed = true;
            }
          }
          return changed ? next : prev;
        });
        setAutoCalculateFields((prev) => {
          const next = { ...prev };
          let changed = false;
          for (const readiness of automationReadiness) {
            const normalized = normalizeAutomationReadiness(readiness);
            const columnKey = String(normalized.columnIndex);
            if (
              readiness.mode === 'autoCalculate' &&
              readiness.isReady &&
              !currentMappings[columnKey] &&
              !fixedValues[columnKey] &&
              !boxAiFields[columnKey] &&
              !next[columnKey] &&
              !declinedAutoCalculateFields[columnKey]
            ) {
              next[columnKey] = true;
              changed = true;
            }
          }
          return changed ? next : prev;
        });
      }
    } catch (err) {
      console.warn('Failed to infer source defaults:', err);
    }
  }, [copiedMappings, fixedValues, boxAiFields, resolvedSourceValues, autoCalculateFields, declinedAutoCalculateFields, declinedAutoGenerateFields]);

  useEffect(() => {
    if (isAutoBuilding) return;
    if (!file || uploadedHeaders.length === 0) {
      setSourceAutomationReadiness({});
      return;
    }

    const timeout = window.setTimeout(() => {
      void applyInferredSourceDefaults(file, mappings, true);
    }, 450);

    return () => window.clearTimeout(timeout);
  }, [file, uploadedHeaders.length, mappings, copiedMappings, resolvedSourceValues, autoCalculateFields, applyInferredSourceDefaults, isAutoBuilding]);

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file to upload.');
      return;
    }

    setIsUploading(true);
    setError(null);
    setUploadSuccess(false);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const uploadController = new AbortController();
      const uploadTimeout = window.setTimeout(() => uploadController.abort(), 60000);
      const response = await fetch(`${API_BASE_URL}/data-tape/upload`, {
        method: 'POST',
        body: formData,
        signal: uploadController.signal,
      });
      window.clearTimeout(uploadTimeout);

      const data: UploadResponse | ErrorResponse = await response.json();

      if (!response.ok) {
        const errorData = data as ErrorResponse;
        throw new Error(errorData.error || 'Failed to upload file');
      }

      const successData = data as UploadResponse;
      if (successData.success && successData.headers) {
        const emptyHeaderSet = new Set(
          Array.isArray(successData.emptyHeaders) ? successData.emptyHeaders : []
        );
        setUploadedHeaders(successData.headers);
        setEmptyHeaders(emptyHeaderSet);
        setUploadSuccess(true);
        setRemovedHeaders(new Set());
        setSoteriaSortOrder('original');
        setChipsSortOrder('original');
        // Upload is done. Keep pairing state separate so UI doesn't look stuck.
        setIsUploading(false);
        
        // Auto-pair columns using OpenAI semantic matching
        setIsPairing(true);
        let matchEmptyHeaders: Set<string> = emptyHeaderSet;
        try {
          const matchController = new AbortController();
          const matchTimeout = window.setTimeout(
            () => matchController.abort(),
            MATCH_COLUMNS_CLIENT_TIMEOUT_MS
          );
          let matchResponse: Response;
          try {
            // Send the file too, so the backend can use sample column values to
            // strengthen the matching (not just header names).
            const matchFormData = new FormData();
            matchFormData.append('file', file);
            matchFormData.append('uploaded_headers', JSON.stringify(successData.headers));
            matchResponse = await fetch(`${API_BASE_URL}/data-tape/match-columns`, {
              method: 'POST',
              body: matchFormData,
              signal: matchController.signal,
            });
          } finally {
            window.clearTimeout(matchTimeout);
          }

          const matchData = await matchResponse.json();
          
          // Console log the full pairing result object (this is what you'll see in browser console)
          console.log('=== FULL PAIRING RESULT OBJECT ===');
          console.log(JSON.stringify(matchData, null, 2));
          console.log('=== END OF PAIRING RESULT ===');
          
          // Additional breakdown for easier inspection
          if (matchData.allMatches && Array.isArray(matchData.allMatches)) {
            console.log('=== ALL MATCHES BREAKDOWN ===');
            console.log(`Total allMatches: ${matchData.allMatches.length}`);
            console.log('Confidence breakdown:', {
              STRONG: matchData.allMatches.filter((m: { confidence: string }) => m.confidence === 'STRONG').length,
              MEDIUM: matchData.allMatches.filter((m: { confidence: string }) => m.confidence === 'MEDIUM').length,
              WEAK: matchData.allMatches.filter((m: { confidence: string }) => m.confidence === 'WEAK').length,
              NONE: matchData.allMatches.filter((m: { confidence: string }) => m.confidence === 'NONE').length,
            });
            console.log('All matches with details:', matchData.allMatches);
            console.log('=== END OF ALL MATCHES BREAKDOWN ===');
          } else {
            console.warn('⚠️ allMatches is missing or not an array:', matchData.allMatches);
          }
          
          console.log('Filtered matches (used for pairing):', matchData.matches?.length || 0);

          matchEmptyHeaders = new Set([
            ...Array.from(emptyHeaderSet),
            ...(Array.isArray(matchData.emptyHeaders) ? matchData.emptyHeaders : []),
          ]);
          if (matchEmptyHeaders.size > 0) {
            setEmptyHeaders(matchEmptyHeaders);
          }

          const useSemantic =
            matchResponse.ok &&
            matchData.success === true &&
            Array.isArray(matchData.matches) &&
            matchData.matches.length > 0;

          if (useSemantic) {
            const autoMappings: Record<string, string> = {};
            matchData.matches.forEach(
              (match: { column_index: number; soteria_field: string; matched_uploaded_column: string }) => {
                const idx = resolveSemanticMatchColumnIndex(match);
                if (
                  typeof idx === 'number' &&
                  idx >= 0 &&
                  idx < SOTERIA_COLUMNS.length &&
                  !matchEmptyHeaders.has(match.matched_uploaded_column)
                ) {
                  autoMappings[String(idx)] = match.matched_uploaded_column;
                }
              }
            );
            setMappings(stripEmptyHeaderMappings(autoMappings, matchEmptyHeaders));
            setResolvedSourceValues({});
            setCopiedMappings({});
            setSourceValidationErrors({});
            await applyInferredSourceDefaults(
              file,
              stripEmptyHeaderMappings(autoMappings, matchEmptyHeaders)
            );
          } else {
            if (!matchResponse.ok) {
              console.warn(
                'match-columns HTTP',
                matchResponse.status,
                '— using local fuzzy pairing (not the OpenAI result).'
              );
            }
            // Fallback: if semantic matching fails or returns no pairs, use local fuzzy matching
            const autoMappings = performAutoPairing(successData.headers, matchEmptyHeaders);
            setMappings(autoMappings);
            setResolvedSourceValues({});
            setCopiedMappings({});
            setSourceValidationErrors({});
            await applyInferredSourceDefaults(file, autoMappings);
          }
        } catch (matchErr) {
          // Often AbortError when MATCH_COLUMNS_CLIENT_TIMEOUT_MS is too low vs gpt-5.4 latency
          if (matchErr instanceof DOMException && matchErr.name === 'AbortError') {
            console.warn(
              `Column matching was aborted after ${MATCH_COLUMNS_CLIENT_TIMEOUT_MS}ms; using local fuzzy pairing (UI will not match server/OpenAI JSON).`
            );
          } else {
            console.warn('Semantic matching failed, falling back to fuzzy matching:', matchErr);
          }
          const autoMappings = performAutoPairing(successData.headers, matchEmptyHeaders);
          setMappings(autoMappings);
          setResolvedSourceValues({});
          setCopiedMappings({});
          setSourceValidationErrors({});
          await applyInferredSourceDefaults(file, autoMappings);
        } finally {
          setIsPairing(false);
        }
      } else {
        throw new Error('Failed to extract headers from file');
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        setError('Upload timed out. Try a smaller file or check backend logs.');
      } else {
        setError(err instanceof Error ? err.message : 'An error occurred while uploading the file.');
      }
      setUploadedHeaders([]);
      setEmptyHeaders(new Set());
      setMappings({});
    } finally {
      setIsUploading(false);
    }
  };

  // Drag handlers for uploaded header chips
  const handleDragStart = (e: React.DragEvent, header: string) => {
    if (emptyHeaders.has(header)) {
      e.preventDefault();
      return;
    }
    setDraggedHeader(header);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', header);
  };

  const handleDragEnd = () => {
    setDraggedHeader(null);
    setDragOverSoteria(null);
  };

  // Drop handlers for Soteria column drop zones
  const handleDragOver = (e: React.DragEvent, columnKey: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverSoteria(columnKey);
  };

  const handleDragLeave = () => {
    setDragOverSoteria(null);
  };

  const handleDrop = (e: React.DragEvent, columnKey: string) => {
    e.preventDefault();
    const header = e.dataTransfer.getData('text/plain');
    
    if (header && emptyHeaders.has(header)) {
      setDragOverSoteria(null);
      return;
    }

    if (header) {
      const resolvedKeysToClear = new Set([
        columnKey,
        ...Object.keys(mappings).filter((key) => mappings[key] === header),
      ]);
      // Clear any fixed value for this column
      setFixedValues((prev) => {
        const newFixed = { ...prev };
        delete newFixed[columnKey];
        return newFixed;
      });
      setAutoGenerateFields((prev) => {
        const next = { ...prev };
        delete next[columnKey];
        return next;
      });
      setAutoCalculateFields((prev) => {
        const next = { ...prev };
        delete next[columnKey];
        return next;
      });
      setBoxAiFields((prev) => {
        const next = { ...prev };
        delete next[columnKey];
        return next;
      });
      // Clear any copied mapping
      setCopiedMappings((prev) => {
        const newCopied = { ...prev };
        delete newCopied[columnKey];
        return newCopied;
      });
      setResolvedSourceValues((prev) => {
        const next = { ...prev };
        resolvedKeysToClear.forEach((key) => {
          delete next[key];
        });
        return next;
      });
      // Close editing mode if open
      if (editingFixedValue === columnKey) {
        setEditingFixedValue(null);
      }
      
      setMappings((prev) => {
        const newMappings = { ...prev };
        // Remove the header from any previous mapping
        Object.keys(newMappings).forEach((key) => {
          if (newMappings[key] === header) {
            delete newMappings[key];
          }
        });
        // Set the new mapping
        newMappings[columnKey] = header;
        return newMappings;
      });
      // Clear download URL if file was already generated
      if (hasGeneratedOnce) {
        setDownloadUrl(null);
      }
    }
    
    setDragOverSoteria(null);
    setDraggedHeader(null);
  };

  // Remove mapping (unpair)
  const handleRemoveMapping = (columnKey: string) => {
    setMappings((prev) => {
      const newMappings = { ...prev };
      delete newMappings[columnKey];
      return newMappings;
    });
    setCopiedMappings((prev) => withoutCopiedMappingReferences(prev, columnKey));
    setResolvedSourceValues((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    setBoxAiFields((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    // Clear download URL if file was already generated
    if (hasGeneratedOnce) {
      setDownloadUrl(null);
    }
  };

  const handleSetAutoGenerate = (columnKey: string) => {
    if (!AUTO_GENERATE_COL_INDICES.has(Number(columnKey))) return;

    // Clear mutually-exclusive modes for this field.
    setMappings((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    setFixedValues((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    setCopiedMappings((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    setResolvedSourceValues((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    setEditingFixedValue((prev) => (prev === columnKey ? null : prev));
    setDeclinedAutoGenerateFields((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    setAutoGenerateFields((prev) => ({
      ...prev,
      [columnKey]: true,
    }));
    setAutoCalculateFields((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    setBoxAiFields((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });

    if (hasGeneratedOnce) {
      setDownloadUrl(null);
    }
  };

  const handleSetAutoCalculate = (columnKey: string) => {
    const colIdx = Number(columnKey);
    if (!AUTO_CALC_COL_INDICES.has(colIdx)) return;

    setMappings((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    setFixedValues((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    setCopiedMappings((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    setResolvedSourceValues((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    setAutoGenerateFields((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    setBoxAiFields((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    setEditingFixedValue((prev) => (prev === columnKey ? null : prev));
    setDeclinedAutoCalculateFields((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    setAutoCalculateFields((prev) => ({
      ...prev,
      [columnKey]: true,
    }));

    if (file) {
      void applyInferredSourceDefaults(file, mappings);
    }

    if (hasGeneratedOnce) {
      setDownloadUrl(null);
    }
  };

  // Handle edit fixed value (pencil icon click)
  const handleEditFixedValue = (columnKey: string) => {
    setAutoGenerateFields((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    setAutoCalculateFields((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    setBoxAiFields((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    // If there's a mapping, remove it and return the chip to the bank
    if (mappings[columnKey]) {
      setMappings((prev) => {
        const newMappings = { ...prev };
        delete newMappings[columnKey];
        return newMappings;
      });
    }
    setResolvedSourceValues((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    // Open the text input for editing
    setEditingFixedValue(columnKey);
    // If there's already a fixed value, we'll show it in the input
    // Clear download URL if file was already generated
    if (hasGeneratedOnce) {
      setDownloadUrl(null);
    }
  };

  // Handle save fixed value
  const handleSaveFixedValue = (columnKey: string, value: string) => {
    if (value.trim() !== '') {
      setFixedValues((prev) => ({
        ...prev,
        [columnKey]: value.trim(),
      }));
    } else {
      // If empty, remove the fixed value
      setFixedValues((prev) => {
        const newFixed = { ...prev };
        delete newFixed[columnKey];
        return newFixed;
      });
    }
    setEditingFixedValue(null);
    setParameterAutocomplete({ open: false, inputValue: '', cursorPosition: 0, filterText: '', source: 'soteria', targetColumnKey: null });
    // Clear download URL if file was already generated
    if (hasGeneratedOnce) {
      setDownloadUrl(null);
    }
  };

  const getParameterTokenCandidates = useMemo(() => {
    const soteria = SOTERIA_COLUMNS.map((fieldName) => `@${fieldName}`);
    const original = uploadedHeaders.map((header) => `@${ORIGINAL_PARAM_PREFIX}${header}`);
    return [...soteria, ...original].sort((a, b) => b.length - a.length);
  }, [uploadedHeaders]);

  // Handle parameter autocomplete input
  const handleParameterInput = (e: React.ChangeEvent<HTMLInputElement>, columnKey: string) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart || 0;
    
    // Find @ symbol before cursor
    const textBeforeCursor = value.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      // Check if @ is not part of a word (no alphanumeric before it)
      const charBeforeAt = lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : ' ';
      if (!/[a-zA-Z0-9]/.test(charBeforeAt)) {
        // Check if we're inside an existing parameter (after @ but before space or next @)
        // If there's a space after the @ and before cursor, we're past the parameter
        const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
        const firstSpaceIndex = textAfterAt.indexOf(' ');
        
        // Only open autocomplete if:
        // 1. No space found after @ (still typing parameter name), OR
        // 2. Space is found but cursor is before the space (still in parameter)
        if (firstSpaceIndex === -1 || cursorPos <= lastAtIndex + 1 + firstSpaceIndex) {
          const rawFilterText = textAfterAt.split(/\s/)[0]; // Get text up to first space
          const isOriginal = rawFilterText.toLowerCase().startsWith(ORIGINAL_PARAM_PREFIX);
          const filterText = isOriginal ? rawFilterText.slice(ORIGINAL_PARAM_PREFIX.length) : rawFilterText;
          setParameterAutocomplete({
            open: true,
            inputValue: value,
            cursorPosition: cursorPos,
            filterText,
            source: isOriginal ? 'original' : 'soteria',
            targetColumnKey: columnKey,
          });
          return;
        }
      }
    }
    
    // Close autocomplete if @ is removed or cursor moves away
    setParameterAutocomplete({ open: false, inputValue: value, cursorPosition: cursorPos, filterText: '', source: 'soteria', targetColumnKey: null });
  };

  // Handle parameter selection
  const handleSelectParameter = (selectedField: string, source: ParameterSource) => {
    if (!parameterAutocomplete.targetColumnKey) return;
    
    const { inputValue, cursorPosition } = parameterAutocomplete;
    const textBeforeCursor = inputValue.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const beforeAt = inputValue.substring(0, lastAtIndex);
      const afterCursor = inputValue.substring(cursorPosition);
      // Add space after parameter to allow continuing to type
      const parameterText = source === 'original'
        ? `@${ORIGINAL_PARAM_PREFIX}${selectedField}`
        : `@${selectedField}`;
      const newValue = `${beforeAt}${parameterText} ${afterCursor}`;
      const newCursorPos = lastAtIndex + parameterText.length + 1; // +1 for the space
      
      const targetCol = parameterAutocomplete.targetColumnKey;
      
      // Close autocomplete first to prevent onChange interference
      setParameterAutocomplete({
        open: false,
        inputValue: newValue,
        cursorPosition: newCursorPos,
        filterText: '',
        source: 'soteria',
        targetColumnKey: null,
      });
      
      // Update React state
      setFixedValues((prev) => ({
        ...prev,
        [targetCol]: newValue,
      }));
      
      // Store cursor position to be set after React updates DOM
      pendingCursorRef.current = { col: targetCol, pos: newCursorPos };
    }
  };

  // Check if cursor is inside a parameter
  // Returns: { inParam: boolean, paramStart: number, paramEnd: number, isAtParamEnd: boolean }
  // isAtParamEnd is true only if cursor is at the last character of the parameter (right before space)
  const isCursorInParameter = (value: string, cursorPos: number): { inParam: boolean; paramStart: number; paramEnd: number; isAtParamEnd: boolean } => {
    const textBeforeCursor = value.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex === -1) {
      return { inParam: false, paramStart: -1, paramEnd: -1, isAtParamEnd: false };
    }
    
    // Check if @ is not part of a word (no alphanumeric before it)
    const charBeforeAt = lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : ' ';
    if (/[a-zA-Z0-9]/.test(charBeforeAt)) {
      return { inParam: false, paramStart: -1, paramEnd: -1, isAtParamEnd: false };
    }
    
    // Try to match against known parameter tokens first
    let matchedLength = 0;
    for (const token of getParameterTokenCandidates) {
      if (value.substring(lastAtIndex).startsWith(token)) {
        const afterParam = value[lastAtIndex + token.length];
        if (!afterParam || /\s/.test(afterParam) || afterParam === '@') {
          if (token.length > matchedLength) {
            matchedLength = token.length;
          }
        }
      }
    }
    
    let paramEnd: number;
    if (matchedLength > 0) {
      // Found exact match
      paramEnd = lastAtIndex + matchedLength;
    } else {
      // No exact match, use greedy matching until space or @
      paramEnd = lastAtIndex + 1;
      while (paramEnd < value.length) {
        if (value[paramEnd] === '@') {
          break;
        }
        if (/\s/.test(value[paramEnd])) {
          break;
        }
        paramEnd++;
      }
    }
    
    // Check if cursor is within the parameter range
    if (cursorPos > lastAtIndex && cursorPos <= paramEnd) {
      // Check if cursor is at the exact end of the parameter (right before the space)
      const isAtParamEnd = cursorPos === paramEnd;
      return { inParam: true, paramStart: lastAtIndex, paramEnd: paramEnd, isAtParamEnd };
    }
    
    return { inParam: false, paramStart: -1, paramEnd: -1, isAtParamEnd: false };
  };

  // Get filtered Soteria columns for autocomplete
  const getFilteredSoteriaColumns = useMemo(() => {
    if (!parameterAutocomplete.filterText) {
      return SOTERIA_COLUMNS;
    }
    const filter = parameterAutocomplete.filterText.toLowerCase();
    return SOTERIA_COLUMNS.filter(col => 
      col.toLowerCase().includes(filter)
    );
  }, [parameterAutocomplete.filterText]);

  const getFilteredOriginalColumns = useMemo(() => {
    if (!parameterAutocomplete.filterText) {
      return uploadedHeaders;
    }
    const filter = parameterAutocomplete.filterText.toLowerCase();
    return uploadedHeaders.filter((col) => col.toLowerCase().includes(filter));
  }, [parameterAutocomplete.filterText, uploadedHeaders]);

  // Handle cancel editing fixed value
  const handleCancelEditFixedValue = () => {
    setEditingFixedValue(null);
  };

  // Handle copy button click
  const handleCopyClick = (columnKey: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setCopyDialog({ open: true, targetColumnKey: columnKey });
  };

  // Handle select copied mapping
  const handleSelectCopiedMapping = (sourceColumnKey: string) => {
    if (!copyDialog.targetColumnKey) return;

    const targetColKey = copyDialog.targetColumnKey;
    const sourceHeader = mappings[sourceColumnKey];

    if (!sourceHeader) return;

    // Clear any fixed value or existing mapping for target
    setFixedValues((prev) => {
      const newFixed = { ...prev };
      delete newFixed[targetColKey];
      return newFixed;
    });
    setAutoGenerateFields((prev) => {
      const next = { ...prev };
      delete next[targetColKey];
      return next;
    });
    setAutoCalculateFields((prev) => {
      const next = { ...prev };
      delete next[targetColKey];
      return next;
    });
    setResolvedSourceValues((prev) => {
      const next = { ...prev };
      delete next[targetColKey];
      return next;
    });

    // Set the copied mapping
    setCopiedMappings((prev) => ({
      ...prev,
      [targetColKey]: sourceColumnKey,
    }));

    // Also set the mapping to the same source header
    setMappings((prev) => ({
      ...prev,
      [targetColKey]: sourceHeader,
    }));

    setCopyDialog({ open: false, targetColumnKey: null });
    
    // Clear download URL if file was already generated
    if (hasGeneratedOnce) {
      setDownloadUrl(null);
    }
  };

  // Get list of paired Soteria columns (for copy dialog)
  const getPairedSoteriaColumns = (): Array<{ columnKey: string; uploadedHeader: string; displayLabel: string }> => {
    return Object.entries(mappings)
      .filter(([columnKey]) => !copiedMappings[columnKey])
      .map(([columnKey, uploadedHeader]) => {
        const idx = Number(columnKey);
        const fieldName = SOTERIA_COLUMNS[idx] ?? columnKey;
        return {
          columnKey,
          uploadedHeader,
          displayLabel: fieldName,
        };
      });
  };

  // Handle Data Room Link button click
  const handleDataRoomLinkClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDataRoomLinkDialog({ open: true });
    setBoxFolderUrl(savedDataRoomLink?.boxFolderUrl || '');
    setMatchResults(null);
  };

  // Handle Match Test for Data Room Link
  const handleMatchTest = async () => {
    if (!file || !boxFolderUrl.trim()) {
      return;
    }

    setIsMatching(true);
    setMatchResults(null);
    setMatchError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('boxFolderUrl', boxFolderUrl.trim());
      formData.append('mappings', JSON.stringify(mappings));

      const response = await fetch(`${API_BASE_URL}/data-tape/match-box-folders`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const allMatches = data.matches || [];
        const excelAddresses = Array.isArray(data.excelAddresses)
          ? data.excelAddresses
          : [];
        const boxFolderNames = Array.isArray(data.boxFolderNames)
          ? data.boxFolderNames
          : [];
        console.log(
          '[Data Room Link — Box Folder Matching] Addresses from Excel:',
          excelAddresses
        );
        console.log(
          '[Data Room Link — Box Folder Matching] Names from Box folder (match candidates):',
          boxFolderNames
        );

        // Console log all match results including those < 30%
        console.log('=== Full Box Folder Match Results ===');
        console.log(`Total addresses: ${data.totalAddresses || 0}`);
        console.log(`Matched (>30%): ${data.matchedCount || 0}`);
        console.log('\nAll matches (including <50%):');
        allMatches.forEach((match: { address: string; folderUrl: string | null; folderName: string | null; similarity: number }, index: number) => {
          const similarityPercent = (match.similarity * 100).toFixed(1);
          const status = match.folderUrl ? '✓ MATCHED' : match.similarity > 0 ? '⚠ LOW SIMILARITY' : '✗ NO MATCH';
          console.log(`\n${index + 1}. ${match.address}`);
          console.log(`   Status: ${status}`);
          if (match.folderName) {
            console.log(`   Folder: ${match.folderName}`);
          }
          if (match.folderUrl) {
            console.log(`   URL: ${match.folderUrl}`);
          }
          console.log(`   Similarity: ${similarityPercent}%`);
        });
        console.log('\n=== End Match Results ===\n');
        
        setMatchResults({
          matches: allMatches,
          totalAddresses: data.totalAddresses || 0,
          matchedCount: data.matchedCount || 0,
        });
        setMatchError(null);
      } else {
        const errorMsg = data.error || 'Match test failed';
        console.error('Match test failed:', errorMsg);
        setMatchError(errorMsg);
        setMatchResults({
          matches: [],
          totalAddresses: 0,
          matchedCount: 0,
        });
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Match test failed';
      console.error('Match test failed:', err);
      setMatchError(errorMsg);
      setMatchResults({
        matches: [],
        totalAddresses: 0,
        matchedCount: 0,
      });
    } finally {
      setIsMatching(false);
    }
  };

  // Handle save Data Room Link
  const handleSaveDataRoomLink = () => {
    if (boxFolderUrl.trim() && matchResults) {
      const savedUrl = boxFolderUrl.trim();
      setSavedDataRoomLink({
        boxFolderUrl: savedUrl,
        matches: matchResults.matches,
      });
      setDataRoomLinkDialog({ open: false });
      // Clear download URL if file was already generated
      if (hasGeneratedOnce) {
        setDownloadUrl(null);
      }
      if (pendingBoxAiColumnKey) {
        const columnKey = pendingBoxAiColumnKey;
        setPendingBoxAiColumnKey(null);
        void fetchBoxAiColumnValues(columnKey, savedUrl);
      }
      if (pendingCompleteFromDataRoom) {
        setPendingCompleteFromDataRoom(false);
        void fetchCompleteFromDataRoom(savedUrl);
      }
    }
  };

  // Handle close Data Room Link dialog
  const handleCloseDataRoomLinkDialog = () => {
    setDataRoomLinkDialog({ open: false });
    if (!savedDataRoomLink) {
      setBoxFolderUrl('');
      setMatchResults(null);
    }
    if (!savedDataRoomLink && pendingBoxAiColumnKey) {
      setPendingBoxAiColumnKey(null);
    }
    if (!savedDataRoomLink && pendingCompleteFromDataRoom) {
      setPendingCompleteFromDataRoom(false);
    }
  };

  const fetchBoxAiColumnValues = async (columnKey: string, dataRoomUrl: string) => {
    if (!file) return;

    const columnIndex = Number.parseInt(columnKey, 10);
    const columnName = SOTERIA_COLUMNS[columnIndex] ?? '';

    setIsBoxAiLoading(true);
    setBoxAiDialog({
      open: false,
      columnKey,
      columnName,
      results: [],
      error: null,
    });

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('boxFolderUrl', dataRoomUrl);
      formData.append('columnIndex', columnKey);
      formData.append('columnName', columnName);
      formData.append('mappings', JSON.stringify(mappings));

      const response = await fetch(`${API_BASE_URL}/data-tape/box-ai-column`, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();

      if (!response.ok || data.success !== true) {
        throw new Error(data.error || 'Box AI extraction failed');
      }

      setBoxAiDialog({
        open: true,
        columnKey,
        columnName,
        results: (data.results as BoxAiColumnResult[]) || [],
        error: null,
      });
    } catch (err) {
      setBoxAiDialog({
        open: true,
        columnKey,
        columnName,
        results: [],
        error: err instanceof Error ? err.message : 'Box AI extraction failed',
      });
    } finally {
      setIsBoxAiLoading(false);
    }
  };

  const handleBoxAiClick = (columnKey: string, e: React.MouseEvent) => {
    e.stopPropagation();

    if (!file) {
      return;
    }

    if (!savedDataRoomLink?.boxFolderUrl?.trim()) {
      setPendingBoxAiColumnKey(columnKey);
      setDataRoomLinkDialog({ open: true });
      setBoxFolderUrl('');
      setMatchResults(null);
      setMatchError(null);
      return;
    }

    void fetchBoxAiColumnValues(columnKey, savedDataRoomLink.boxFolderUrl);
  };

  const handleCloseBoxAiDialog = () => {
    if (isBoxAiLoading) return;
    setBoxAiDialog({
      open: false,
      columnKey: null,
      columnName: '',
      results: [],
      error: null,
    });
  };

  const handleConfirmBoxAi = () => {
    const { columnKey, results } = boxAiDialog;
    if (!columnKey) return;

    const addressReplacements = Object.fromEntries(
      results
        .filter((r) => r.value !== null && r.value.trim() !== '')
        .map((r) => [r.address, r.value!.trim()])
    );

    if (Object.keys(addressReplacements).length === 0) return;

    setMappings((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    setFixedValues((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    setCopiedMappings((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    setAutoGenerateFields((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    setAutoCalculateFields((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    setEditingFixedValue((prev) => (prev === columnKey ? null : prev));

    // Store the Box AI base values (address-keyed) and reset any prior corrections for
    // this column so conflicts are re-evaluated against the fresh values.
    setBoxAiValues((prev) => ({
      ...prev,
      [columnKey]: addressReplacements,
    }));
    setBoxAiResults((prev) => ({
      ...prev,
      [columnKey]: results,
    }));
    setResolvedSourceValues((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    setBoxAiFields((prev) => ({
      ...prev,
      [columnKey]: true,
    }));

    if (hasGeneratedOnce) {
      setDownloadUrl(null);
    }

    handleCloseBoxAiDialog();
  };

  const handleRemoveBoxAi = (columnKey: string) => {
    setBoxAiFields((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    setBoxAiValues((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    setBoxAiResults((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    setResolvedSourceValues((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    setBoxAiPreviewDialog((prev) =>
      prev.columnKey === columnKey ? { open: false, columnKey: null, columnName: '' } : prev
    );
    if (hasGeneratedOnce) {
      setDownloadUrl(null);
    }
  };

  const handleOpenBoxAiPreview = (columnKey: string) => {
    const columnIndex = Number.parseInt(columnKey, 10);
    setBoxAiPreviewDialog({
      open: true,
      columnKey,
      columnName: SOTERIA_COLUMNS[columnIndex] ?? '',
    });
  };

  const handleCloseBoxAiPreview = () => {
    setBoxAiPreviewDialog({ open: false, columnKey: null, columnName: '' });
  };

  // Build the rows for the Box AI preview dialog: the effective value actually used
  // (with any conflict corrections applied) plus the source file each value came from.
  const boxAiPreviewRows = useMemo(() => {
    const columnKey = boxAiPreviewDialog.columnKey;
    if (!columnKey) return [] as Array<{
      address: string;
      value: string | null;
      fileName: string | null;
      fileUrl: string | null;
      error: string | null;
    }>;
    const baseValues = boxAiValues[columnKey] || {};
    const corrections = resolvedSourceValues[columnKey] || {};
    const storedResults = boxAiResults[columnKey] || [];
    const resultByAddress = new Map(storedResults.map((r) => [r.address, r]));

    // Prefer the order of the stored results; fall back to the applied values map.
    const addresses =
      storedResults.length > 0
        ? storedResults.map((r) => r.address)
        : Object.keys(baseValues);

    return addresses.map((address) => {
      const stored = resultByAddress.get(address);
      const base = baseValues[address];
      const used = base !== undefined ? corrections[base] ?? base : null;
      return {
        address,
        value: used,
        fileName: used ? stored?.fileName ?? null : null,
        fileUrl: used ? stored?.fileUrl ?? null : null,
        error: used ? null : stored?.error ?? 'Not used',
      };
    });
  }, [boxAiPreviewDialog.columnKey, boxAiValues, resolvedSourceValues, boxAiResults]);

  // A column counts as "populated" if it has any value source assigned.
  const isColumnPopulated = useCallback(
    (columnKey: string): boolean => {
      return Boolean(
        mappings[columnKey] ||
          fixedValues[columnKey] ||
          copiedMappings[columnKey] ||
          autoGenerateFields[columnKey] ||
          autoCalculateFields[columnKey] ||
          boxAiFields[columnKey]
      );
    },
    [mappings, fixedValues, copiedMappings, autoGenerateFields, autoCalculateFields, boxAiFields]
  );

  const getUnpopulatedCompletableIndices = useCallback((): number[] => {
    const indices: number[] = [];
    for (let i = 0; i < SOTERIA_COLUMNS.length; i++) {
      if (COMPLETE_FROM_DATA_ROOM_EXCLUDED.has(i)) continue;
      if (isColumnPopulated(String(i))) continue;
      indices.push(i);
    }
    return indices;
  }, [isColumnPopulated]);

  const fetchCompleteFromDataRoom = async (dataRoomUrl: string) => {
    if (!file) return;

    const columnIndices = getUnpopulatedCompletableIndices();
    if (columnIndices.length === 0) {
      setCompleteDialog({
        open: true,
        columns: [],
        activeColumnIndex: null,
        excludedColumnKeys: [],
        error: 'There are no unpopulated columns to complete.',
      });
      return;
    }

    setIsCompleteLoading(true);
    setCompleteDialog({ open: false, columns: [], activeColumnIndex: null, excludedColumnKeys: [], error: null });

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('boxFolderUrl', dataRoomUrl);
      formData.append('columnIndices', JSON.stringify(columnIndices));
      formData.append('mappings', JSON.stringify(mappings));

      const response = await fetch(`${API_BASE_URL}/data-tape/box-ai-complete`, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();

      if (!response.ok || data.success !== true) {
        throw new Error(data.error || 'Box AI completion failed');
      }

      const columns = (data.columns as BoxAiMultiColumnResult[]) || [];
      const firstWithValues = columns.find((c) =>
        c.results.some((r) => r.value !== null && r.value.trim() !== '')
      );
      setCompleteDialog({
        open: true,
        columns,
        activeColumnIndex: (firstWithValues ?? columns[0])?.columnIndex ?? null,
        excludedColumnKeys: [],
        error: null,
      });
    } catch (err) {
      setCompleteDialog({
        open: true,
        columns: [],
        activeColumnIndex: null,
        excludedColumnKeys: [],
        error: err instanceof Error ? err.message : 'Box AI completion failed',
      });
    } finally {
      setIsCompleteLoading(false);
    }
  };

  const handleCompleteFromDataRoomClick = () => {
    if (!file) return;
    setCompleteConfirmOpen(true);
  };

  const handleConfirmCompleteFromDataRoom = () => {
    setCompleteConfirmOpen(false);
    if (!file) return;

    if (!savedDataRoomLink?.boxFolderUrl?.trim()) {
      setPendingCompleteFromDataRoom(true);
      setDataRoomLinkDialog({ open: true });
      setBoxFolderUrl('');
      setMatchResults(null);
      setMatchError(null);
      return;
    }

    void fetchCompleteFromDataRoom(savedDataRoomLink.boxFolderUrl);
  };

  const handleCloseCompleteDialog = () => {
    if (isCompleteLoading) return;
    setCompleteDialog({ open: false, columns: [], activeColumnIndex: null, excludedColumnKeys: [], error: null });
  };

  const handleConfirmCompleteDialog = () => {
    const excluded = new Set(completeDialog.excludedColumnKeys);
    const columnsWithValues = completeDialog.columns.filter(
      (c) =>
        !excluded.has(String(c.columnIndex)) &&
        c.results.some((r) => r.value !== null && r.value.trim() !== '')
    );
    if (columnsWithValues.length === 0) return;

    const newBoxAiValues: Record<string, Record<string, string>> = {};
    const newBoxAiResults: Record<string, BoxAiColumnResult[]> = {};
    const newBoxAiFields: Record<string, true> = {};
    const keysToClear = new Set<string>();

    for (const column of columnsWithValues) {
      const columnKey = String(column.columnIndex);
      const addressReplacements = Object.fromEntries(
        column.results
          .filter((r) => r.value !== null && r.value.trim() !== '')
          .map((r) => [r.address, r.value!.trim()])
      );
      if (Object.keys(addressReplacements).length === 0) continue;
      newBoxAiValues[columnKey] = addressReplacements;
      newBoxAiResults[columnKey] = column.results;
      newBoxAiFields[columnKey] = true;
      keysToClear.add(columnKey);
    }

    if (Object.keys(newBoxAiFields).length === 0) return;

    setMappings((prev) => {
      const next = { ...prev };
      keysToClear.forEach((key) => delete next[key]);
      return next;
    });
    setFixedValues((prev) => {
      const next = { ...prev };
      keysToClear.forEach((key) => delete next[key]);
      return next;
    });
    setCopiedMappings((prev) => {
      const next = { ...prev };
      keysToClear.forEach((key) => delete next[key]);
      return next;
    });
    setAutoGenerateFields((prev) => {
      const next = { ...prev };
      keysToClear.forEach((key) => delete next[key]);
      return next;
    });
    setAutoCalculateFields((prev) => {
      const next = { ...prev };
      keysToClear.forEach((key) => delete next[key]);
      return next;
    });
    // Reset any prior corrections for these columns; conflicts are re-evaluated fresh.
    setResolvedSourceValues((prev) => {
      const next = { ...prev };
      keysToClear.forEach((key) => delete next[key]);
      return next;
    });

    setBoxAiValues((prev) => ({ ...prev, ...newBoxAiValues }));
    setBoxAiResults((prev) => ({ ...prev, ...newBoxAiResults }));
    setBoxAiFields((prev) => ({ ...prev, ...newBoxAiFields }));

    if (hasGeneratedOnce) {
      setDownloadUrl(null);
    }

    handleCloseCompleteDialog();
  };


  // Get unpaired headers (headers not currently mapped and not removed)
  const unpairedHeaders = useMemo(() => {
    const pairedHeaders = new Set(Object.values(mappings));
    return uploadedHeaders.filter(
      (header) => !pairedHeaders.has(header) && !removedHeaders.has(header)
    );
  }, [uploadedHeaders, mappings, removedHeaders]);

  // Sort Soteria columns based on current sort order - memoized to prevent duplicates
  // Use array of {name, originalIndex} to maintain unique keys even with duplicate names
  const sortedSoteriaColumns = useMemo(() => {
    // Create array with original indices
    const columnsWithIndex = SOTERIA_COLUMNS.map((name, index) => ({ name, originalIndex: index }));
    
    if (soteriaSortOrder === 'a-z') {
      return columnsWithIndex.sort((a, b) => a.name.localeCompare(b.name));
    } else if (soteriaSortOrder === 'z-a') {
      return columnsWithIndex.sort((a, b) => b.name.localeCompare(a.name));
    }
    return columnsWithIndex; // original order
  }, [soteriaSortOrder]);

  // Sort chips: columns with data first, empty columns below; sort within each group.
  const sortedChips = useMemo(() => {
    const withValue = unpairedHeaders.filter((header) => !emptyHeaders.has(header));
    const empty = unpairedHeaders.filter((header) => emptyHeaders.has(header));

    const sortGroup = (headers: string[]) => {
      if (chipsSortOrder === 'a-z') {
        return [...headers].sort((a, b) => a.localeCompare(b));
      }
      if (chipsSortOrder === 'z-a') {
        return [...headers].sort((a, b) => b.localeCompare(a));
      }
      const order = new Map(uploadedHeaders.map((header, index) => [header, index]));
      return [...headers].sort(
        (a, b) => (order.get(a) ?? Number.MAX_SAFE_INTEGER) - (order.get(b) ?? Number.MAX_SAFE_INTEGER)
      );
    };

    return [...sortGroup(withValue), ...sortGroup(empty)];
  }, [chipsSortOrder, unpairedHeaders, emptyHeaders, uploadedHeaders]);

  // Cycle through sort orders
  const cycleSortOrder = (current: 'original' | 'a-z' | 'z-a'): 'original' | 'a-z' | 'z-a' => {
    if (current === 'original') return 'a-z';
    if (current === 'a-z') return 'z-a';
    return 'original';
  };

  // Remove header from unpaired bank completely
  const handleRemoveFromBank = (header: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent drag from starting
    setRemovedHeaders((prev) => {
      const newSet = new Set(prev);
      newSet.add(header);
      return newSet;
    });
  };

  // Handle preview column values
  const handlePreviewColumn = async (columnName: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent drag from starting
    if (!file) {
      return;
    }

    setIsLoadingPreview(true);
    setPreviewDialog({ open: true, column: columnName, values: [] });

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('column_name', columnName);

      const response = await fetch(`${API_BASE_URL}/data-tape/column-values`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (data.success && data.values) {
        setPreviewDialog({ open: true, column: columnName, values: data.values });
      } else {
        setPreviewDialog({ open: true, column: columnName, values: [] });
      }
    } catch (err) {
      console.error('Failed to load column values:', err);
      setPreviewDialog({ open: true, column: columnName, values: [] });
    } finally {
      setIsLoadingPreview(false);
    }
  };

  // Close preview dialog
  const handleClosePreview = () => {
    setPreviewDialog({ open: false, column: null, values: [] });
  };

  const validateSourceConflictOverride = (
    value: string,
    result: SourceConflictResolution | null
  ): string | null => {
    const allowedValues = result?.allowedValues || [];
    const rule = result?.validationRule;
    return validateFieldValue(value, allowedValues, rule);
  };

  const updateSourceConflictOverride = (
    sourceValue: string,
    value: string,
    result: SourceConflictResolution | null
  ) => {
    const validationError = validateSourceConflictOverride(value, result);
    setSourceConflictOverrides((prev) => ({
      ...prev,
      [sourceValue]: value,
    }));
    setSourceConflictOverrideErrors((prev) => {
      const next = { ...prev };
      if (validationError) {
        next[sourceValue] = validationError;
      } else {
        delete next[sourceValue];
      }
      return next;
    });
  };

  const handleResolveSourceConflict = async (columnKey: string) => {
    if (!file) return;

    const fieldName = SOTERIA_COLUMNS[Number.parseInt(columnKey, 10)] ?? '';

    setIsResolvingSourceConflict(true);
    setResolvingConflictColumnKey(columnKey);
    setSourceConflictDialog({ open: true, columnKey, result: null, error: null });
    setSourceConflictOverrides({});
    setSourceConflictOverrideErrors({});

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('columnIndex', columnKey);
      if (fieldName) {
        formData.append('field', fieldName);
      }
      formData.append('mappings', JSON.stringify(mappings));
      formData.append('copiedMappings', JSON.stringify(copiedMappings));
      formData.append('resolvedSourceValues', JSON.stringify(resolvedSourceValues));
      formData.append('boxAiFields', JSON.stringify(Object.keys(boxAiFields)));
      formData.append('boxAiValues', JSON.stringify(boxAiValues));

      const response = await fetch(`${API_BASE_URL}/data-tape/resolve-source-conflicts`, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();

      if (!response.ok || data.success !== true) {
        throw new Error(data.error || 'Failed to resolve conflicts.');
      }

      const result = data as SourceConflictResolution;
      setSourceConflictOverrides(
        Object.fromEntries(
          result.suggestions.map((suggestion) => [
            suggestion.source_value,
            suggestion.suggested_value || '',
          ])
        )
      );
      setSourceConflictOverrideErrors(
        Object.fromEntries(
          result.suggestions
            .map((suggestion) => {
              const value = suggestion.suggested_value || '';
              const validationError = validateSourceConflictOverride(value, result);
              return validationError ? [suggestion.source_value, validationError] as const : null;
            })
            .filter((entry): entry is readonly [string, string] => entry !== null)
        )
      );
      setSourceConflictDialog({
        open: true,
        columnKey,
        result,
        error: null,
      });
    } catch (err) {
      setSourceConflictOverrides({});
      setSourceConflictOverrideErrors({});
      setSourceConflictDialog({
        open: true,
        columnKey,
        result: null,
        error: err instanceof Error ? err.message : 'Failed to resolve conflicts.',
      });
    } finally {
      setIsResolvingSourceConflict(false);
      setResolvingConflictColumnKey(null);
    }
  };

  const handleCloseSourceConflictDialog = () => {
    if (isResolvingSourceConflict) return;
    setSourceConflictDialog({ open: false, columnKey: null, result: null, error: null });
    setSourceConflictOverrides({});
    setSourceConflictOverrideErrors({});
  };

  const handleCloseResolvedSourceDialog = () => {
    setResolvedSourceDialog({ open: false, columnKey: null });
  };

  const handleApplySourceConflictResolution = () => {
    const result = sourceConflictDialog.result;
    if (!result) return;

    const replacements = Object.fromEntries(
      result.suggestions
        .map((suggestion) => [
          suggestion.source_value,
          (sourceConflictOverrides[suggestion.source_value] || suggestion.suggested_value || '').trim(),
        ])
        .filter(([sourceValue, resolvedValue]) => resolvedValue !== '' && !sourceConflictOverrideErrors[sourceValue])
    );

    if (Object.keys(replacements).length === 0) return;

    const columnKey = String(result.columnIndex);
    setResolvedSourceValues((prev) => ({
      ...prev,
      [columnKey]: {
        ...(prev[columnKey] || {}),
        ...replacements,
      },
    }));
    setDownloadUrl(null);
    setSourceConflictDialog({ open: false, columnKey: null, result: null, error: null });
    setSourceConflictOverrides({});
    setSourceConflictOverrideErrors({});
  };

  // Auto-pair columns using fuzzy matching (>50% similarity)
  const performAutoPairing = (
    headers: string[],
    excludeHeaders: ReadonlySet<string> = new Set()
  ): Record<string, string> => {
    const autoMappings: Record<string, string> = {};
    const usedHeaders = new Set<string>();
    const SIMILARITY_THRESHOLD = 0.5;
    const pairableHeaders = headers.filter((header) => !excludeHeaders.has(header));

    for (let i = 0; i < SOTERIA_COLUMNS.length; i++) {
      const soteriaCol = SOTERIA_COLUMNS[i]!;
      let bestHeader = '';
      let bestScore = SIMILARITY_THRESHOLD;

      for (const header of pairableHeaders) {
        if (usedHeaders.has(header)) {
          continue;
        }

        const similarity = stringSimilarity(soteriaCol, header);

        if (similarity > bestScore) {
          bestHeader = header;
          bestScore = similarity;
        }
      }

      if (bestHeader !== '') {
        autoMappings[String(i)] = bestHeader;
        usedHeaders.add(bestHeader);
      }
    }

    return autoMappings;
  };

  // Generate Soteria Data Tape
  const handleGenerate = async () => {
    if (!file) {
      setGenerateError('No file available. Please upload a file first.');
      return;
    }

    const pairedCount = Object.keys(mappings).length;
    const fixedCount = Object.keys(fixedValues).length;
    const autoGenerateCount = Object.keys(autoGenerateFields).length;
    const autoCalculateCount = Object.keys(autoCalculateFields).length;
    const boxAiCount = Object.keys(boxAiFields).length;
    if (pairedCount === 0 && fixedCount === 0 && autoGenerateCount === 0 && autoCalculateCount === 0 && boxAiCount === 0) {
      setGenerateError('No columns are paired, fixed values set, auto-calculated, auto-generated, or filled via Box AI. Please configure at least one value before generating.');
      return;
    }
    if (isValidatingSource) {
      setGenerateError('Imported values are still being validated. Please wait a moment and try again.');
      return;
    }
    if (sourceValidationColumnErrorCount > 0) {
      setGenerateError('Fix imported value validation errors before generating the Soteria Data Tape.');
      return;
    }

    setIsGenerating(true);
    setGenerateError(null);
    setDownloadUrl(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('mappings', JSON.stringify(mappings));
    formData.append('fixedValues', JSON.stringify(fixedValues));
    formData.append('copiedMappings', JSON.stringify(copiedMappings));
    formData.append('resolvedSourceValues', JSON.stringify(resolvedSourceValues));
    formData.append('autoGenerateFields', JSON.stringify(Object.keys(autoGenerateFields)));
    formData.append('autoCalculateFields', JSON.stringify(Object.keys(autoCalculateFields)));
    formData.append('boxAiFields', JSON.stringify(Object.keys(boxAiFields)));
    formData.append('boxAiValues', JSON.stringify(boxAiValues));
    if (savedDataRoomLink) {
      formData.append('dataRoomLink', JSON.stringify({
        boxFolderUrl: savedDataRoomLink.boxFolderUrl,
        matches: savedDataRoomLink.matches,
      }));
    }

    try {
      const response = await fetch(`${API_BASE_URL}/data-tape/generate`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.code === 'SOURCE_VALIDATION_FAILED' && data.sourceValidation?.errorsByColumn) {
          setSourceValidationErrors(data.sourceValidation.errorsByColumn);
        }
        throw new Error(data.error || 'Failed to generate Data Tape');
      }

      if (data.success && data.downloadUrl) {
        setDownloadUrl(`${API_BASE_URL}${data.downloadUrl}`);
        setHasGeneratedOnce(true);
      } else {
        throw new Error('Failed to generate Data Tape');
      }
    } catch (err) {
      setGenerateError(err instanceof Error ? err.message : 'An error occurred while generating the Data Tape.');
    } finally {
      setIsGenerating(false);
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // Automated "Upload & Generate Datatape" orchestration
  // ──────────────────────────────────────────────────────────────────────────

  const setAutoStep = (
    id: string,
    status: AutoBuildStepStatus,
    detail?: string
  ) => {
    setAutoBuild((prev) => ({
      ...prev,
      steps: prev.steps.map((step) =>
        step.id === id ? { ...step, status, detail: detail ?? step.detail } : step
      ),
    }));
  };

  type AutoGenerateConfig = {
    mappings: Record<string, string>;
    fixedValues: Record<string, string>;
    copiedMappings: Record<string, string>;
    resolvedSourceValues: Record<string, Record<string, string>>;
    autoGenerate: string[];
    autoCalculate: string[];
    boxAiFields: string[];
    boxAiValues: Record<string, Record<string, string>>;
    dataRoomLink: { boxFolderUrl: string; matches: MatchResult[] } | null;
  };

  const apiExtractHeaders = async (
    uploadFile: File
  ): Promise<{ headers: string[]; emptyHeaders: Set<string> }> => {
    const fd = new FormData();
    fd.append('file', uploadFile);
    const res = await fetch(`${API_BASE_URL}/data-tape/upload`, { method: 'POST', body: fd });
    const data = await res.json();
    if (!res.ok || data.success !== true || !Array.isArray(data.headers)) {
      throw new Error(data.error || 'Failed to extract headers from the file.');
    }
    return {
      headers: data.headers as string[],
      emptyHeaders: new Set(
        Array.isArray(data.emptyHeaders) ? (data.emptyHeaders as string[]) : []
      ),
    };
  };

  const apiMatchColumns = async (
    uploadFile: File,
    headers: string[],
    excludeHeaders: ReadonlySet<string> = new Set()
  ): Promise<Record<string, string>> => {
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      fd.append('uploaded_headers', JSON.stringify(headers));
      const res = await fetch(`${API_BASE_URL}/data-tape/match-columns`, {
        method: 'POST',
        body: fd,
      });
      const data = await res.json();
      if (res.ok && data.success === true && Array.isArray(data.matches) && data.matches.length > 0) {
        const emptyFromApi = new Set(
          Array.isArray(data.emptyHeaders) ? (data.emptyHeaders as string[]) : []
        );
        const skipHeaders = new Set([
          ...Array.from(excludeHeaders),
          ...Array.from(emptyFromApi),
        ]);
        const result: Record<string, string> = {};
        for (const match of data.matches as Array<{
          column_index: number;
          soteria_field?: string;
          matched_uploaded_column: string;
        }>) {
          const idx = resolveSemanticMatchColumnIndex(match);
          if (
            typeof idx === 'number' &&
            idx >= 0 &&
            idx < SOTERIA_COLUMNS.length &&
            !skipHeaders.has(match.matched_uploaded_column)
          ) {
            result[String(idx)] = match.matched_uploaded_column;
          }
        }
        return stripEmptyHeaderMappings(result, skipHeaders);
      }
    } catch {
      // fall through to fuzzy matching
    }
    return performAutoPairing(headers, excludeHeaders);
  };

  const apiValidateConfig = async (
    uploadFile: File,
    cfg: {
      mappings: Record<string, string>;
      copiedMappings?: Record<string, string>;
      resolvedSourceValues?: Record<string, Record<string, string>>;
      autoGenerate?: string[];
      autoCalculate?: string[];
      boxAiFields?: string[];
      boxAiValues?: Record<string, Record<string, string>>;
    }
  ): Promise<SourceValidationResponse> => {
    const fd = new FormData();
    fd.append('file', uploadFile);
    fd.append('mappings', JSON.stringify(cfg.mappings));
    fd.append('copiedMappings', JSON.stringify(cfg.copiedMappings || {}));
    fd.append('resolvedSourceValues', JSON.stringify(cfg.resolvedSourceValues || {}));
    fd.append('autoGenerateFields', JSON.stringify(cfg.autoGenerate || []));
    fd.append('autoCalculateFields', JSON.stringify(cfg.autoCalculate || []));
    fd.append('boxAiFields', JSON.stringify(cfg.boxAiFields || []));
    fd.append('boxAiValues', JSON.stringify(cfg.boxAiValues || {}));
    const res = await fetch(`${API_BASE_URL}/data-tape/validate-source-mappings`, {
      method: 'POST',
      body: fd,
    });
    const data = await res.json();
    if (!res.ok || data.success !== true || !data.sourceValidation) {
      throw new Error(data.error || 'Failed to validate imported values.');
    }
    return data.sourceValidation as SourceValidationResponse;
  };

  const apiResolveConflicts = async (
    uploadFile: File,
    cfg: {
      columnIndex: string;
      field?: string;
      mappings: Record<string, string>;
      copiedMappings?: Record<string, string>;
      resolvedSourceValues?: Record<string, Record<string, string>>;
      boxAiFields?: string[];
      boxAiValues?: Record<string, Record<string, string>>;
    }
  ): Promise<SourceConflictResolution> => {
    const fd = new FormData();
    fd.append('file', uploadFile);
    fd.append('columnIndex', cfg.columnIndex);
    const fieldName =
      cfg.field?.trim() ||
      SOTERIA_COLUMNS[Number.parseInt(cfg.columnIndex, 10)]?.trim() ||
      '';
    if (fieldName) {
      fd.append('field', fieldName);
    }
    fd.append('mappings', JSON.stringify(cfg.mappings));
    fd.append('copiedMappings', JSON.stringify(cfg.copiedMappings || {}));
    fd.append('resolvedSourceValues', JSON.stringify(cfg.resolvedSourceValues || {}));
    fd.append('boxAiFields', JSON.stringify(cfg.boxAiFields || []));
    fd.append('boxAiValues', JSON.stringify(cfg.boxAiValues || {}));
    const res = await fetch(`${API_BASE_URL}/data-tape/resolve-source-conflicts`, {
      method: 'POST',
      body: fd,
    });
    const data = await res.json();
    if (!res.ok || data.success !== true) {
      throw new Error(data.error || 'Failed to resolve conflicts.');
    }
    return data as SourceConflictResolution;
  };

  const apiInferDefaults = async (
    uploadFile: File,
    cfg: {
      mappings: Record<string, string>;
      copiedMappings?: Record<string, string>;
      fixedValues?: Record<string, string>;
      resolvedSourceValues?: ResolvedSourceValues;
      autoCalculate?: string[];
    }
  ): Promise<{
    suggestions: SourceDefaultInferenceSuggestion[];
    automationReadiness: SourceAutomationReadiness[];
  }> => {
    const fd = new FormData();
    fd.append('file', uploadFile);
    fd.append('mappings', JSON.stringify(cfg.mappings));
    fd.append('fieldMappings', JSON.stringify(buildFieldMappings(cfg.mappings)));
    fd.append('copiedMappings', JSON.stringify(cfg.copiedMappings || {}));
    fd.append('fixedValues', JSON.stringify(cfg.fixedValues || {}));
    fd.append('resolvedSourceValues', JSON.stringify(cfg.resolvedSourceValues || {}));
    if (cfg.autoCalculate) {
      fd.append('autoCalculateFields', JSON.stringify(cfg.autoCalculate));
    }
    const res = await fetch(`${API_BASE_URL}/data-tape/infer-source-defaults`, {
      method: 'POST',
      body: fd,
    });
    const data = await res.json();
    if (!res.ok || data.success !== true) {
      return { suggestions: [], automationReadiness: [] };
    }
    return {
      suggestions: Array.isArray(data.suggestions) ? data.suggestions : [],
      automationReadiness: Array.isArray(data.automationReadiness) ? data.automationReadiness : [],
    };
  };

  const apiMatchBoxFolders = async (
    uploadFile: File,
    url: string,
    currentMappings: Record<string, string>
  ): Promise<MatchResult[]> => {
    const fd = new FormData();
    fd.append('file', uploadFile);
    fd.append('boxFolderUrl', url);
    fd.append('mappings', JSON.stringify(currentMappings));
    const res = await fetch(`${API_BASE_URL}/data-tape/match-box-folders`, {
      method: 'POST',
      body: fd,
    });
    const data = await res.json();
    if (!res.ok || data.success !== true) {
      throw new Error(data.error || 'Failed to match Data Room folders.');
    }
    return Array.isArray(data.matches) ? (data.matches as MatchResult[]) : [];
  };

  const apiBoxAiComplete = async (
    uploadFile: File,
    url: string,
    columnIndices: number[],
    currentMappings: Record<string, string>
  ): Promise<BoxAiMultiColumnResult[]> => {
    const fd = new FormData();
    fd.append('file', uploadFile);
    fd.append('boxFolderUrl', url);
    fd.append('columnIndices', JSON.stringify(columnIndices));
    fd.append('mappings', JSON.stringify(currentMappings));
    const res = await fetch(`${API_BASE_URL}/data-tape/box-ai-complete`, {
      method: 'POST',
      body: fd,
    });
    const data = await res.json();
    if (!res.ok || data.success !== true) {
      throw new Error(data.error || 'Box AI completion failed.');
    }
    return Array.isArray(data.columns) ? (data.columns as BoxAiMultiColumnResult[]) : [];
  };

  const apiGenerateDataTape = async (
    uploadFile: File,
    cfg: AutoGenerateConfig
  ): Promise<string> => {
    const fd = new FormData();
    fd.append('file', uploadFile);
    fd.append('mappings', JSON.stringify(cfg.mappings));
    fd.append('fixedValues', JSON.stringify(cfg.fixedValues));
    fd.append('copiedMappings', JSON.stringify(cfg.copiedMappings));
    fd.append('resolvedSourceValues', JSON.stringify(cfg.resolvedSourceValues));
    fd.append('autoGenerateFields', JSON.stringify(cfg.autoGenerate));
    fd.append('autoCalculateFields', JSON.stringify(cfg.autoCalculate));
    fd.append('boxAiFields', JSON.stringify(cfg.boxAiFields));
    fd.append('boxAiValues', JSON.stringify(cfg.boxAiValues));
    if (cfg.dataRoomLink) {
      fd.append(
        'dataRoomLink',
        JSON.stringify({
          boxFolderUrl: cfg.dataRoomLink.boxFolderUrl,
          matches: cfg.dataRoomLink.matches,
        })
      );
    }
    const res = await fetch(`${API_BASE_URL}/data-tape/generate`, { method: 'POST', body: fd });
    const data = await res.json();
    if (!res.ok || data.success !== true || !data.downloadUrl) {
      throw new Error(data.error || 'Failed to generate the Soteria Data Tape.');
    }
    return `${API_BASE_URL}${data.downloadUrl}`;
  };

  const triggerBrowserDownload = (url: string) => {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'soteria-data-tape.xlsx';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  };

  const handleUploadAndGenerateClick = () => {
    if (!file) {
      setError('Please select a file to upload.');
      return;
    }
    autoBuildAbortRef.current = false;
    autoBuildConflictResolverRef.current = null;
    setAutoBuildConflicts([]);
    setAutoBuildConflictInputs({});
    setAutoBuildConflictErrors({});
    setAutoBuildUrlInput(savedDataRoomLink?.boxFolderUrl || '');
    setAutoBuild({
      phase: 'collect-url',
      steps: AUTO_BUILD_STEP_DEFS.map((step) => ({ ...step, status: 'pending' as AutoBuildStepStatus })),
      error: null,
      downloadUrl: null,
    });
  };

  const cancelAutoBuild = () => {
    autoBuildAbortRef.current = true;
    if (autoBuildConflictResolverRef.current) {
      autoBuildConflictResolverRef.current({});
      autoBuildConflictResolverRef.current = null;
    }
    setIsAutoBuilding(false);
    setAutoBuild({ phase: 'idle', steps: [], error: null, downloadUrl: null });
    setAutoBuildConflicts([]);
    setAutoBuildConflictInputs({});
    setAutoBuildConflictErrors({});
  };

  const updateAutoBuildConflictInput = (
    columnKey: string,
    sourceValue: string,
    value: string,
    resolution: SourceConflictResolution
  ) => {
    const validationError = validateSourceConflictOverride(value, resolution);
    setAutoBuildConflictInputs((prev) => ({
      ...prev,
      [columnKey]: { ...(prev[columnKey] || {}), [sourceValue]: value },
    }));
    setAutoBuildConflictErrors((prev) => {
      const next = { ...prev };
      const colErrors = { ...(next[columnKey] || {}) };
      if (validationError) {
        colErrors[sourceValue] = validationError;
      } else {
        delete colErrors[sourceValue];
      }
      next[columnKey] = colErrors;
      return next;
    });
  };

  const handleAutoBuildConflictsContinue = () => {
    const errors: Record<string, Record<string, string>> = {};
    let hasError = false;
    for (const conflict of autoBuildConflicts) {
      const columnKey = String(conflict.columnIndex);
      const inputs = autoBuildConflictInputs[columnKey] || {};
      for (const suggestion of conflict.suggestions) {
        const value = inputs[suggestion.source_value] || '';
        const validationError = validateSourceConflictOverride(value, conflict);
        if (validationError) {
          errors[columnKey] = errors[columnKey] || {};
          errors[columnKey][suggestion.source_value] = validationError;
          hasError = true;
        }
      }
    }
    if (hasError) {
      setAutoBuildConflictErrors(errors);
      return;
    }
    const resolver = autoBuildConflictResolverRef.current;
    autoBuildConflictResolverRef.current = null;
    if (resolver) {
      resolver(autoBuildConflictInputs);
    }
  };

  const runAutoBuild = async (dataRoomUrl: string) => {
    if (!file) return;
    const uploadFile = file;
    setIsAutoBuilding(true);
    setAutoBuild((prev) => ({ ...prev, phase: 'running', error: null }));

    const localMappings: Record<string, string> = {};
    const localCopied: Record<string, string> = {};
    const localFixed: Record<string, string> = {};
    const localAutoGen: Record<string, true> = {};
    const localAutoCalc: Record<string, true> = {};
    const localBoxAiFields: Record<string, true> = {};
    const localBoxAiValues: Record<string, Record<string, string>> = {};
    const localBoxAiResults: Record<string, BoxAiColumnResult[]> = {};
    const localResolved: Record<string, Record<string, string>> = {};
    let localDataRoom: { boxFolderUrl: string; matches: MatchResult[] } | null = null;
    let headers: string[] = [];
    let extractedEmptyHeaders = new Set<string>();

    const aborted = () => autoBuildAbortRef.current;

    try {
      // 1. Extract headers
      setAutoStep('extract', 'active');
      const extracted = await apiExtractHeaders(uploadFile);
      headers = extracted.headers;
      extractedEmptyHeaders = extracted.emptyHeaders;
      if (aborted()) return;
      setAutoStep('extract', 'done', `${headers.length} headers extracted`);

      // 2. Map columns
      setAutoStep('map', 'active');
      const mapped = await apiMatchColumns(uploadFile, headers, extractedEmptyHeaders);
      if (aborted()) return;
      Object.assign(localMappings, mapped);
      setAutoStep('map', 'done', `${Object.keys(localMappings).length} columns mapped`);

      // 3. Resolve conflicts (auto-resolve MEDIUM+, then manual for the rest)
      setAutoStep('resolve', 'active', 'Checking mapped values');
      const validation = await apiValidateConfig(uploadFile, {
        mappings: localMappings,
        copiedMappings: localCopied,
        resolvedSourceValues: localResolved,
      });
      if (aborted()) return;

      const conflictedColumns = Object.keys(validation.errorsByColumn || {});
      const manualConflicts: SourceConflictResolution[] = [];
      let autoResolvedCount = 0;
      for (const columnKey of conflictedColumns) {
        if (aborted()) return;
        const resolution = await apiResolveConflicts(uploadFile, {
          columnIndex: columnKey,
          field: SOTERIA_COLUMNS[Number.parseInt(columnKey, 10)] ?? undefined,
          mappings: localMappings,
          copiedMappings: localCopied,
          resolvedSourceValues: localResolved,
        });
        const suggestions = resolution.suggestions || [];
        const resolvedKey = String(resolution.columnIndex);
        const allConfident =
          suggestions.length > 0 &&
          suggestions.every(
            (s) => s.suggested_value !== null && (s.confidence === 'HIGH' || s.confidence === 'MEDIUM')
          );
        if (allConfident) {
          const map: Record<string, string> = {};
          for (const s of suggestions) {
            if (s.suggested_value) map[s.source_value] = s.suggested_value;
          }
          localResolved[resolvedKey] = { ...(localResolved[resolvedKey] || {}), ...map };
          autoResolvedCount += 1;
        } else {
          manualConflicts.push(resolution);
        }
      }

      if (manualConflicts.length > 0) {
        const inputs: Record<string, Record<string, string>> = {};
        for (const conflict of manualConflicts) {
          const columnKey = String(conflict.columnIndex);
          inputs[columnKey] = {};
          for (const s of conflict.suggestions) {
            inputs[columnKey][s.source_value] = s.suggested_value || '';
          }
        }
        setAutoBuildConflicts(manualConflicts);
        setAutoBuildConflictInputs(inputs);
        setAutoBuildConflictErrors({});
        setAutoStep(
          'resolve',
          'active',
          `Auto-resolved ${autoResolvedCount}; ${manualConflicts.length} need your input`
        );
        setAutoBuild((prev) => ({ ...prev, phase: 'resolve-conflicts' }));

        const userResolved = await new Promise<Record<string, Record<string, string>>>(
          (resolve) => {
            autoBuildConflictResolverRef.current = resolve;
          }
        );
        if (aborted()) return;
        for (const [columnKey, valueMap] of Object.entries(userResolved)) {
          const filtered: Record<string, string> = {};
          for (const [sourceValue, value] of Object.entries(valueMap)) {
            if (value && value.trim() !== '') filtered[sourceValue] = value.trim();
          }
          if (Object.keys(filtered).length > 0) {
            localResolved[columnKey] = { ...(localResolved[columnKey] || {}), ...filtered };
          }
        }
        setAutoBuild((prev) => ({ ...prev, phase: 'running' }));
        setAutoBuildConflicts([]);
      }
      setAutoStep(
        'resolve',
        'done',
        conflictedColumns.length === 0
          ? 'No conflicts found'
          : `${conflictedColumns.length} column(s) resolved`
      );

      // 4 & 5. Auto-fill deducible columns + automation readiness
      setAutoStep('autofill', 'active');
      const inference = await apiInferDefaults(uploadFile, {
        mappings: localMappings,
        copiedMappings: localCopied,
        fixedValues: localFixed,
      });
      if (aborted()) return;
      for (const suggestion of inference.suggestions) {
        const columnKey = String(suggestion.columnIndex);
        if (!localMappings[columnKey] && !localFixed[columnKey]) {
          localFixed[columnKey] = suggestion.value;
        }
      }
      setAutoStep('autofill', 'done', `${Object.keys(localFixed).length} columns auto-filled`);

      setAutoStep('automation', 'active');
      for (const readiness of inference.automationReadiness) {
        const normalized = normalizeAutomationReadiness(readiness);
        const columnKey = String(normalized.columnIndex);
        if (localMappings[columnKey] || localFixed[columnKey]) continue;
        if (!normalized.isReady) continue;
        if (normalized.mode === 'autoGenerate') {
          localAutoGen[columnKey] = true;
        } else if (normalized.mode === 'autoCalculate') {
          localAutoCalc[columnKey] = true;
        }
      }
      setAutoStep(
        'automation',
        'done',
        `${Object.keys(localAutoGen).length + Object.keys(localAutoCalc).length} automated field(s)`
      );

      // 6. Box AI for the remaining unpopulated columns
      setAutoStep('boxai', 'active', 'Matching Data Room property folders');
      const matches = await apiMatchBoxFolders(uploadFile, dataRoomUrl, localMappings);
      if (aborted()) return;
      localDataRoom = { boxFolderUrl: dataRoomUrl, matches };

      const boxAiIndices: number[] = [];
      for (let i = 0; i < SOTERIA_COLUMNS.length; i++) {
        if (COMPLETE_FROM_DATA_ROOM_EXCLUDED.has(i)) continue;
        const columnKey = String(i);
        if (
          localMappings[columnKey] ||
          localFixed[columnKey] ||
          localCopied[columnKey] ||
          localAutoGen[columnKey] ||
          localAutoCalc[columnKey] ||
          localBoxAiFields[columnKey]
        ) {
          continue;
        }
        boxAiIndices.push(i);
      }

      if (boxAiIndices.length > 0) {
        setAutoStep('boxai', 'active', `Extracting ${boxAiIndices.length} columns from the Data Room`);
        const columns = await apiBoxAiComplete(
          uploadFile,
          dataRoomUrl,
          boxAiIndices,
          localMappings
        );
        if (aborted()) return;
        for (const column of columns) {
          const columnKey = String(column.columnIndex);
          const map: Record<string, string> = {};
          for (const result of column.results) {
            if (result.value !== null && result.value.trim() !== '') {
              map[result.address] = result.value.trim();
            }
          }
          if (Object.keys(map).length > 0) {
            localBoxAiValues[columnKey] = map;
            localBoxAiResults[columnKey] = column.results;
            localBoxAiFields[columnKey] = true;
          }
        }

        // Silently reconcile Box AI conflicts: auto-resolve MEDIUM+, then drop values
        // that still cannot be aligned so the generation step isn't blocked.
        if (Object.keys(localBoxAiFields).length > 0) {
          setAutoStep('boxai', 'active', 'Aligning Box AI values with column types');
          const boxAiValidation = await apiValidateConfig(uploadFile, {
            mappings: localMappings,
            copiedMappings: localCopied,
            resolvedSourceValues: localResolved,
            autoGenerate: Object.keys(localAutoGen),
            autoCalculate: Object.keys(localAutoCalc),
            boxAiFields: Object.keys(localBoxAiFields),
            boxAiValues: localBoxAiValues,
          });
          const boxAiConflictCols = Object.keys(boxAiValidation.errorsByColumn || {}).filter(
            (columnKey) => localBoxAiFields[columnKey]
          );
          for (const columnKey of boxAiConflictCols) {
            if (aborted()) return;
            const resolution = await apiResolveConflicts(uploadFile, {
              columnIndex: columnKey,
              field: SOTERIA_COLUMNS[Number.parseInt(columnKey, 10)] ?? undefined,
              mappings: localMappings,
              copiedMappings: localCopied,
              resolvedSourceValues: localResolved,
              boxAiFields: Object.keys(localBoxAiFields),
              boxAiValues: localBoxAiValues,
            });
            const map: Record<string, string> = {};
            for (const s of resolution.suggestions || []) {
              if (s.suggested_value !== null && (s.confidence === 'HIGH' || s.confidence === 'MEDIUM')) {
                map[s.source_value] = s.suggested_value;
              }
            }
            if (Object.keys(map).length > 0) {
              const resolvedKey = String(resolution.columnIndex);
              localResolved[resolvedKey] = { ...(localResolved[resolvedKey] || {}), ...map };
            }
          }

          const finalValidation = await apiValidateConfig(uploadFile, {
            mappings: localMappings,
            copiedMappings: localCopied,
            resolvedSourceValues: localResolved,
            autoGenerate: Object.keys(localAutoGen),
            autoCalculate: Object.keys(localAutoCalc),
            boxAiFields: Object.keys(localBoxAiFields),
            boxAiValues: localBoxAiValues,
          });
          for (const [columnKey, columnErrors] of Object.entries(
            finalValidation.errorsByColumn || {}
          )) {
            if (!localBoxAiFields[columnKey]) continue;
            const badValues = new Set(columnErrors.map((e) => e.value));
            const colMap = localBoxAiValues[columnKey] || {};
            for (const [address, value] of Object.entries(colMap)) {
              const effective = (localResolved[columnKey] || {})[value] ?? value;
              if (badValues.has(value) || badValues.has(effective)) {
                delete colMap[address];
              }
            }
            if (Object.keys(colMap).length === 0) {
              delete localBoxAiValues[columnKey];
              delete localBoxAiFields[columnKey];
              delete localBoxAiResults[columnKey];
            } else {
              localBoxAiValues[columnKey] = colMap;
            }
          }
        }
      }
      setAutoStep(
        'boxai',
        'done',
        `${Object.keys(localBoxAiFields).length} column(s) filled from the Data Room`
      );

      // 7. Generate
      setAutoStep('generate', 'active');
      const url = await apiGenerateDataTape(uploadFile, {
        mappings: localMappings,
        fixedValues: localFixed,
        copiedMappings: localCopied,
        resolvedSourceValues: localResolved,
        autoGenerate: Object.keys(localAutoGen),
        autoCalculate: Object.keys(localAutoCalc),
        boxAiFields: Object.keys(localBoxAiFields),
        boxAiValues: localBoxAiValues,
        dataRoomLink: localDataRoom,
      });
      if (aborted()) return;
      setAutoStep('generate', 'done');

      // 8. Download
      setAutoStep('download', 'active');
      triggerBrowserDownload(url);
      setAutoStep('download', 'done');

      // Commit all state so the manual UI reflects the automated build.
      setUploadedHeaders(headers);
      setEmptyHeaders(extractedEmptyHeaders);
      setUploadSuccess(true);
      setRemovedHeaders(new Set());
      setSoteriaSortOrder('original');
      setChipsSortOrder('original');
      setMappings(localMappings);
      setFixedValues(localFixed);
      setCopiedMappings(localCopied);
      setAutoGenerateFields(localAutoGen);
      setAutoCalculateFields(localAutoCalc);
      setBoxAiFields(localBoxAiFields);
      setBoxAiValues(localBoxAiValues);
      setBoxAiResults(localBoxAiResults);
      setResolvedSourceValues(localResolved);
      if (localDataRoom) setSavedDataRoomLink(localDataRoom);
      setHasGeneratedOnce(true);
      setDownloadUrl(url);

      setAutoBuild((prev) => ({ ...prev, phase: 'done', downloadUrl: url }));
    } catch (err) {
      if (autoBuildAbortRef.current) return;
      const message = err instanceof Error ? err.message : 'Automated build failed.';
      setAutoBuild((prev) => ({
        ...prev,
        phase: 'error',
        error: message,
        steps: prev.steps.map((step) =>
          step.status === 'active' ? { ...step, status: 'error' as AutoBuildStepStatus } : step
        ),
      }));
    } finally {
      setIsAutoBuilding(false);
      autoBuildConflictResolverRef.current = null;
    }
  };

  const confirmAutoBuildUrl = () => {
    const url = autoBuildUrlInput.trim();
    if (!url) return;
    void runAutoBuild(url);
  };

  // Reset auto-resolution attempts when the source mapping context changes, so new
  // conflicts created by a different mapping are re-evaluated.
  useEffect(() => {
    autoResolveAttemptedRef.current = new Set();
  }, [file, mappings, copiedMappings, boxAiValues]);

  // In the manual flow, silently auto-resolve any conflicted column whose values can ALL
  // be translated with HIGH confidence. Lower-confidence conflicts are left for the user.
  useEffect(() => {
    if (isAutoBuilding || !file) return;
    const errorColumns = Object.keys(sourceValidationErrors);
    const toTry = errorColumns.filter((ck) => !autoResolveAttemptedRef.current.has(ck));
    if (toTry.length === 0) return;
    toTry.forEach((ck) => autoResolveAttemptedRef.current.add(ck));

    const uploadFile = file;
    let cancelled = false;
    void (async () => {
      const updates: Record<string, Record<string, string>> = {};
      for (const columnKey of toTry) {
        try {
          const resolution = await apiResolveConflicts(uploadFile, {
            columnIndex: columnKey,
            field: SOTERIA_COLUMNS[Number.parseInt(columnKey, 10)] ?? undefined,
            mappings,
            copiedMappings,
            resolvedSourceValues,
            boxAiFields: Object.keys(boxAiFields),
            boxAiValues,
          });
          const suggestions = resolution.suggestions || [];
          const allHigh =
            suggestions.length > 0 &&
            suggestions.every((s) => s.suggested_value !== null && s.confidence === 'HIGH');
          if (allHigh) {
            const map: Record<string, string> = {};
            for (const s of suggestions) {
              if (s.suggested_value) map[s.source_value] = s.suggested_value;
            }
            if (Object.keys(map).length > 0) {
              updates[String(resolution.columnIndex)] = map;
            }
          }
        } catch {
          // ignore; the user can still resolve manually
        }
      }
      if (cancelled || Object.keys(updates).length === 0) return;
      setResolvedSourceValues((prev) => {
        const next = { ...prev };
        for (const [columnKey, map] of Object.entries(updates)) {
          next[columnKey] = { ...(next[columnKey] || {}), ...map };
        }
        return next;
      });
      if (hasGeneratedOnce) setDownloadUrl(null);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sourceValidationErrors,
    file,
    mappings,
    copiedMappings,
    resolvedSourceValues,
    boxAiFields,
    boxAiValues,
    isAutoBuilding,
  ]);

  return (
    <div className="ContainerExcelBuilder">
      <header className="ContainerExcelBuilder-header">
        <Link to="/" className="back-link">
          <img 
            src="https://app.soteriamarket.com/banner3.jpeg" 
            alt="Soteria Logo" 
            className="logo"
          />
        </Link>
        <h1>{isBoxDataroomMode ? 'Generate Datatape From Box Data Room (Single Project)' : 'Container Data Tape Builder'}</h1>
      </header>
      
      <main className="ContainerExcelBuilder-main">
        <div className="excel-builder-container">
          {/* File Upload Section */}
          <div className="upload-section">
            <h2>{isBoxDataroomMode ? 'Upload Source Excel' : 'Upload Data Tape File'}</h2>
            <p className="upload-description">
              {isBoxDataroomMode
                ? 'Upload your bank loan Excel (.xlsx, .xls, .xlsm) or CSV (.csv), then generate a complete Soteria Data Tape automatically using your Box Data Room.'
                : 'Upload an Excel (.xlsx, .xls, .xlsm) or CSV (.csv) file to extract column headers and map them to Soteria data tape columns.'}
            </p>
            
            <div className="file-input-wrapper">
              <input
                type="file"
                id="data-tape-file"
                accept=".xlsx,.xls,.xlsm,.csv"
                onChange={handleFileChange}
                disabled={isUploading}
                className="file-input"
              />
              <label htmlFor="data-tape-file" className="file-label">
                {file ? file.name : 'Choose File'}
              </label>
            </div>

            <div className="upload-buttons-row">
              {!isBoxDataroomMode && (
                <button
                  onClick={handleUpload}
                  disabled={!file || isUploading || isAutoBuilding}
                  className="upload-button"
                >
                  {isUploading ? 'Uploading...' : 'Upload & Extract Headers'}
                </button>
              )}
              <button
                onClick={handleUploadAndGenerateClick}
                disabled={!file || isUploading || isAutoBuilding}
                className="upload-generate-button"
                title="Automatically map, complete, and generate the Soteria Data Tape"
              >
                {isAutoBuilding
                  ? 'Generating…'
                  : isBoxDataroomMode
                    ? 'Generate Datatape'
                    : 'Upload & Generate Datatape'}
              </button>
            </div>

            {error && (
              <div className="error-message">{error}</div>
            )}

            {uploadSuccess && (
              <div className="success-message">
                ✓ File uploaded successfully! {uploadedHeaders.length} column headers extracted.
              </div>
            )}
          </div>

          {/* Mapping Section */}
          {uploadedHeaders.length > 0 && (
            <div className="mapping-section">
              <div className="mapping-section-header">
                <div>
                  <h2>Map Columns</h2>
                  <p className="mapping-description">
                    Drag column chips from the right to drop them next to Soteria columns to create mappings.
                  </p>
                </div>
                <div className="generate-area">
                  <div className="generate-buttons-container">
                    <button
                      onClick={handleGenerate}
                      disabled={
                        isGenerating ||
                        isValidatingSource ||
                        sourceValidationColumnErrorCount > 0 ||
                        (
                          Object.keys(mappings).length === 0 &&
                          Object.keys(fixedValues).length === 0 &&
                          Object.keys(autoGenerateFields).length === 0 &&
                          Object.keys(autoCalculateFields).length === 0 &&
                          Object.keys(boxAiFields).length === 0
                        )
                      }
                      className="generate-button"
                    >
                      {isGenerating && <span className="generate-button-spinner" aria-hidden="true" />}
                      {isGenerating ? 'Generating...' : 'Generate Soteria Data Tape'}
                    </button>
                    {downloadUrl && (
                      <a
                        href={downloadUrl}
                        className="download-datatape-button"
                        download="soteria-data-tape.xlsx"
                      >
                        📥 Download Soteria Data Tape
                      </a>
                    )}
                  </div>
                  {generateError && (
                    <div className="generate-error">{generateError}</div>
                  )}
                  {isGenerating && (
                    <div className="generate-progress-status" role="status" aria-live="polite">
                      <span className="generate-progress-spinner" aria-hidden="true" />
                      Creating Soteria Data Tape. This may take a moment.
                    </div>
                  )}
                  {sourceValidationError && (
                    <div className="generate-error">{sourceValidationError}</div>
                  )}
                  {sourceValidationColumnErrorCount > 0 && (
                    <div className="source-validation-summary">
                      {sourceValidationColumnErrorCount} mapped column{sourceValidationColumnErrorCount === 1 ? '' : 's'} with value/type conflicts must be fixed before generating.
                    </div>
                  )}
                  {resolvedSourceColumnCount > 0 && (
                    <div className="source-resolved-summary">
                      {resolvedSourceColumnCount} mapped column{resolvedSourceColumnCount === 1 ? '' : 's'} using resolved source values.
                    </div>
                  )}
                </div>
              </div>

              <div className="mapping-container">
                {/* Left Column - Soteria Columns with Drop Zones */}
                <div className="mapping-column left-column">
                  <div className="column-header-with-sort">
                    <h3>Soteria Data Tape Columns</h3>
                    <div className="column-header-actions">
                      <button
                        className="complete-from-data-room-button"
                        onClick={handleCompleteFromDataRoomClick}
                        disabled={!file || isCompleteLoading || isBoxAiLoading}
                        title="Use Box AI to complete all unpopulated columns from the Data Room"
                        type="button"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 2a4 4 0 0 1 4 4v1h1a3 3 0 0 1 3 3v2a3 3 0 0 1-3 3h-1v3a4 4 0 0 1-8 0v-3H7a3 3 0 0 1-3-3V10a3 3 0 0 1 3-3h1V6a4 4 0 0 1 4-4z"></path>
                          <circle cx="9" cy="11" r="1"></circle>
                          <circle cx="15" cy="11" r="1"></circle>
                        </svg>
                        Complete From Data Room
                      </button>
                      <button
                        className="sort-button"
                        onClick={() => setSoteriaSortOrder(cycleSortOrder(soteriaSortOrder))}
                        title={`Sort: ${soteriaSortOrder === 'original' ? 'Original' : soteriaSortOrder === 'a-z' ? 'A→Z' : 'Z→A'}`}
                      >
                        {soteriaSortOrder === 'original' && '⇅'}
                        {soteriaSortOrder === 'a-z' && '↑'}
                        {soteriaSortOrder === 'z-a' && '↓'}
                      </button>
                    </div>
                  </div>
                  <div className="column-list">
                    {sortedSoteriaColumns.map(({ name, originalIndex }) => {
                      const soteriaCol = name;
                      const columnKey = String(originalIndex);
                      const displayLabel = soteriaCol;
                      const mappedHeader = mappings[columnKey];
                      const fixedValue = fixedValues[columnKey];
                      const fixedValueOptions =
                        originalIndex === SECTOR_COL_INDEX &&
                        (
                          fixedValues[String(INDUSTRY_COL_INDEX)] === 'Real Estate' ||
                          REAL_ESTATE_SECTORS.includes(fixedValue || '')
                        )
                          ? REAL_ESTATE_SECTORS
                          : soteriaCol === 'State of Incorporation (Guarantor)'
                            ? RESTRICTED_FIELD_VALUES['State of Incorporation']
                          : RESTRICTED_FIELD_VALUES[soteriaCol];
                      const isAutoGenerated = Boolean(autoGenerateFields[columnKey]);
                      const isAutoCalculated = Boolean(autoCalculateFields[columnKey]);
                      const isBoxAi = Boolean(boxAiFields[columnKey]);
                      const isEditing = editingFixedValue === columnKey;
                      const isDragOver = dragOverSoteria === columnKey;
                      const validationErrors = sourceValidationErrors[columnKey] || [];
                      const conflictFieldLabel = validationErrors[0]?.field?.trim() || displayLabel;
                      const automationReadiness = automationReadinessForColumn(
                        sourceAutomationReadiness,
                        columnKey,
                        soteriaCol
                      );
                      const automationWarning =
                        (isAutoCalculated || isAutoGenerated) &&
                        automationReadiness &&
                        !automationReadiness.isReady
                          ? automationReadiness
                          : null;
                      const resolvedValueCount = Object.keys(resolvedSourceValues[columnKey] || {}).length;
                      const copySourceKey = copiedMappings[columnKey];
                      const copySourceIdx = copySourceKey !== undefined ? Number(copySourceKey) : NaN;
                      const copySourceDisplay =
                        copySourceKey !== undefined && Number.isFinite(copySourceIdx) && copySourceIdx >= 0
                          ? SOTERIA_COLUMNS[copySourceIdx] ?? ''
                          : copySourceKey;
                      const groupTitle =
                        soteriaSortOrder === 'original'
                          ? SOTERIA_COLUMN_GROUP_BY_INDEX.get(originalIndex)
                          : undefined;
                      
                      return (
                        <React.Fragment key={`soteria-${originalIndex}`}>
                          {groupTitle && (
                            <div className="soteria-group-title">
                              {groupTitle}
                            </div>
                          )}
                          <div
                            className={`mapping-row ${isDragOver ? 'drag-over' : ''} ${mappedHeader || fixedValue || isAutoGenerated || isAutoCalculated || isBoxAi ? 'paired' : ''} ${validationErrors.length > 0 ? 'source-validation-error-row' : ''} ${automationWarning ? 'automation-warning-row' : ''}`}
                            onDragOver={(e) => handleDragOver(e, columnKey)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, columnKey)}
                          >
                            <div className="soteria-column-label-container">
                              <label className="soteria-column-label" title={displayLabel}>
                                {displayLabel}
                              </label>
                              {resolvedValueCount > 0 && (
                                <button
                                  type="button"
                                  className="source-resolved-indicator"
                                  title={`${resolvedValueCount} source value${resolvedValueCount === 1 ? '' : 's'} resolved for this mapped column`}
                                  aria-label={`Show ${resolvedValueCount} resolved source value${resolvedValueCount === 1 ? '' : 's'} for ${displayLabel}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setResolvedSourceDialog({ open: true, columnKey });
                                  }}
                                >
                                  ✓
                                </button>
                              )}
                              {originalIndex === DATA_ROOM_LINK_COL_INDEX ? (
                                <button
                                  className="data-room-link-button"
                                  onClick={handleDataRoomLinkClick}
                                  title="Set Box folder link"
                                  aria-label="Set Box folder link"
                                  type="button"
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                                  </svg>
                                </button>
                              ) : (
                                <>
                                <button
                                  className="box-ai-button"
                                  onClick={(e) => handleBoxAiClick(columnKey, e)}
                                  title="Get from Box AI"
                                  aria-label="Get from Box AI"
                                  type="button"
                                  disabled={isBoxAiLoading}
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 2a4 4 0 0 1 4 4v1h1a3 3 0 0 1 3 3v2a3 3 0 0 1-3 3h-1v3a4 4 0 0 1-8 0v-3H7a3 3 0 0 1-3-3V10a3 3 0 0 1 3-3h1V6a4 4 0 0 1 4-4z"></path>
                                    <circle cx="9" cy="11" r="1"></circle>
                                    <circle cx="15" cy="11" r="1"></circle>
                                  </svg>
                                </button>
                                <button
                                  className="edit-fixed-value-button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditFixedValue(columnKey);
                                  }}
                                  title="Set fixed value"
                                  aria-label="Set fixed value"
                                  type="button"
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                  </svg>
                                </button>
                                <button
                                  className="copy-mapping-button"
                                  onClick={(e) => handleCopyClick(columnKey, e)}
                                  title="Copy from another column"
                                  aria-label="Copy from another column"
                                  type="button"
                                  disabled={getPairedSoteriaColumns().length === 0}
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                  </svg>
                                </button>
                                {AUTO_GENERATE_COL_INDICES.has(originalIndex) && (
                                  <button
                                    className="auto-generate-button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSetAutoGenerate(columnKey);
                                    }}
                                    title="Auto Generate"
                                    aria-label="Auto Generate"
                                    type="button"
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6L12 3z"></path>
                                      <path d="M18 14l.9 2.1L21 17l-2.1.9L18 20l-.9-2.1L15 17l2.1-.9L18 14z"></path>
                                    </svg>
                                  </button>
                                )}
                                {AUTO_CALC_COL_INDICES.has(originalIndex) && (
                                  <button
                                    className="auto-calculate-button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSetAutoCalculate(columnKey);
                                    }}
                                    title="Auto Calculate"
                                    aria-label="Auto Calculate"
                                    type="button"
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <rect x="4" y="4" width="16" height="16" rx="2"></rect>
                                      <path d="M9 9h6v6H9z"></path>
                                      <path d="M9 1v3M15 1v3M9 20v3M15 20v3M20 9h3M20 14h3M1 9h3M1 14h3"></path>
                                    </svg>
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                          <div className="drop-zone">
                            {isEditing ? (
                              <div className="fixed-value-input-container">
                                {fixedValueOptions ? (
                                  <select
                                    className="fixed-value-select"
                                    value={fixedValue || ''}
                                    autoFocus
                                    onChange={(e) => {
                                      if (e.target.value) {
                                        handleSaveFixedValue(columnKey, e.target.value);
                                      } else {
                                        // Clear the fixed value if "-- Select a value --" is selected
                                        setFixedValues((prev) => {
                                          const newFixed = { ...prev };
                                          delete newFixed[columnKey];
                                          return newFixed;
                                        });
                                        setEditingFixedValue(null);
                                      }
                                    }}
                                    onBlur={(e) => {
                                      if (!e.target.value) {
                                        handleCancelEditFixedValue();
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Escape') {
                                        handleCancelEditFixedValue();
                                      }
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <option value="">-- Select a value --</option>
                                    {fixedValueOptions.map((value) => (
                                      <option key={value} value={value}>
                                        {value}
                                      </option>
                                    ))}
                                  </select>
                                ) : DATE_FIELDS.has(soteriaCol) ? (
                                  <input
                                    type="date"
                                    className="fixed-value-input fixed-value-date-input"
                                    value={fixedValue || ''}
                                    autoFocus
                                    onChange={(e) => {
                                      const dateValue = e.target.value; // Already in YYYY-MM-DD format
                                      if (dateValue) {
                                        handleSaveFixedValue(columnKey, dateValue);
                                      } else {
                                        // Clear the fixed value if date is cleared
                                        setFixedValues((prev) => {
                                          const newFixed = { ...prev };
                                          delete newFixed[columnKey];
                                          return newFixed;
                                        });
                                        setEditingFixedValue(null);
                                      }
                                    }}
                                    onBlur={(e) => {
                                      if (!e.target.value) {
                                        handleCancelEditFixedValue();
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Escape') {
                                        handleCancelEditFixedValue();
                                      }
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                ) : (
                                  <div className="fixed-value-input-wrapper">
                                    <div className="fixed-value-input-container-with-preview">
                                      <input
                                        ref={(el) => {
                                          if (el) {
                                            inputRefs.current[columnKey] = el;
                                          }
                                        }}
                                        type="text"
                                        className={`fixed-value-input ${fixedValue && fixedValue.includes('@') ? 'has-parameters' : ''}`}
                                        data-soteria-col={columnKey}
                                        placeholder="Enter fixed value for all rows... Use @ to insert field parameters"
                                        value={fixedValue || ''}
                                        autoFocus
                                        onChange={(e) => {
                                          // Always handle parameter input to update filter when typing
                                          // This is safe because handleParameterInput only updates autocomplete state
                                          handleParameterInput(e, columnKey);
                                          // Always update fixed value for display
                                          setFixedValues((prev) => ({
                                            ...prev,
                                            [columnKey]: e.target.value,
                                          }));
                                          // Sync preview scroll after text changes
                                          setTimeout(() => syncPreviewScroll(columnKey), 0);
                                        }}
                                        onBlur={(e) => {
                                          // Delay to allow parameter selection
                                          setTimeout(() => {
                                            if (!parameterAutocomplete.open) {
                                              handleSaveFixedValue(columnKey, e.target.value);
                                            }
                                          }, 200);
                                        }}
                                        onKeyDown={(e) => {
                                          if (parameterAutocomplete.open) {
                                            if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter') {
                                              e.preventDefault();
                                              // Handle keyboard navigation (simplified - just close on Enter)
                                              const activeOptions = parameterAutocomplete.source === 'original'
                                                ? getFilteredOriginalColumns
                                                : getFilteredSoteriaColumns;
                                              if (e.key === 'Enter' && activeOptions.length > 0) {
                                                handleSelectParameter(activeOptions[0], parameterAutocomplete.source);
                                              }
                                              return;
                                            }
                                            if (e.key === 'Escape') {
                                              setParameterAutocomplete({ open: false, inputValue: '', cursorPosition: 0, filterText: '', source: 'soteria', targetColumnKey: null });
                                              return;
                                            }
                                          }
                                          
                                          // Handle backspace to delete entire parameter (only if at the end of parameter)
                                          if (e.key === 'Backspace' && !parameterAutocomplete.open) {
                                            const input = e.currentTarget;
                                            const value = input.value;
                                            const cursorPos = input.selectionStart || 0;
                                            
                                            // Only check if cursor is at position > 0 (backspace only works if there's something to delete)
                                            if (cursorPos > 0) {
                                              const paramInfo = isCursorInParameter(value, cursorPos);
                                              
                                              // Only delete entire parameter if cursor is at the exact end of the parameter
                                              // (right before the space that follows it)
                                              if (paramInfo.inParam && paramInfo.isAtParamEnd) {
                                                e.preventDefault();
                                                // Delete the entire parameter (including the trailing space if present)
                                                const afterParam = value.substring(paramInfo.paramEnd);
                                                // Check if there's a space after the parameter and remove it too
                                                const spaceAfter = afterParam.match(/^\s+/);
                                                const spaceLength = spaceAfter ? spaceAfter[0].length : 0;
                                                const afterParamWithoutSpace = afterParam.substring(spaceLength);
                                                
                                                const beforeParam = value.substring(0, paramInfo.paramStart);
                                                const newValue = beforeParam + afterParamWithoutSpace;
                                                const newCursorPos = paramInfo.paramStart;
                                                
                                                setFixedValues((prev) => ({
                                                  ...prev,
                                                  [columnKey]: newValue,
                                                }));
                                                
                                                // Use pending cursor to set position after React re-renders
                                                pendingCursorRef.current = { col: columnKey, pos: newCursorPos };
                                                
                                                // Clear download URL if file was already generated
                                                if (hasGeneratedOnce) {
                                                  setDownloadUrl(null);
                                                }
                                                return;
                                              }
                                            }
                                            // Otherwise, let default backspace behavior work (delete one char)
                                          }
                                          
                                          if (e.key === 'Enter' && !parameterAutocomplete.open) {
                                            handleSaveFixedValue(columnKey, e.currentTarget.value);
                                          } else if (e.key === 'Escape' && !parameterAutocomplete.open) {
                                            handleCancelEditFixedValue();
                                          }
                                        }}
                                        onScroll={() => syncPreviewScroll(columnKey)}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      {fixedValue && fixedValue.includes('@') && (
                                        <div
                                          className="fixed-value-preview"
                                          ref={(el) => {
                                            if (el) previewRefs.current[columnKey] = el;
                                          }}
                                        >
                                          {(() => {
                                            // Split by @ but keep the @ symbol with the field name
                                            const parts: Array<{ text: string; isParam: boolean }> = [];
                                            let currentText = '';
                                            let i = 0;
                                            
                                            while (i < fixedValue.length) {
                                              if (fixedValue[i] === '@' && (i === 0 || !/[a-zA-Z0-9]/.test(fixedValue[i - 1]))) {
                                                // Found @ at start or after non-alphanumeric
                                                if (currentText) {
                                                  parts.push({ text: currentText, isParam: false });
                                                  currentText = '';
                                                }
                                                
                                                // Find the end of the parameter
                                                // Parameter is @FieldName where FieldName can contain spaces
                                                // Parameter ends at: first space (always), next @, or end of string
                                                // But we need to allow spaces WITHIN the field name
                                                // So we match against known Soteria field names
                                                
                                                // Try to match against known Soteria field names (longest match first)
                                                let matchedField: string | null = null;
                                                let matchedLength = 0;
                                                
                                                for (const fieldName of SOTERIA_COLUMNS) {
                                                  const paramText = `@${fieldName}`;
                                                  if (fixedValue.substring(i).startsWith(paramText)) {
                                                    // Check if it's followed by space, @, or end
                                                    const afterParam = fixedValue[i + paramText.length];
                                                    if (!afterParam || /\s/.test(afterParam) || afterParam === '@') {
                                                      if (paramText.length > matchedLength) {
                                                        matchedField = fieldName;
                                                        matchedLength = paramText.length;
                                                      }
                                                    }
                                                  }
                                                }
                                                
                                                if (matchedField) {
                                                  // Found a matching field name
                                                  const param = `@${matchedField}`;
                                                  parts.push({ text: param, isParam: true });
                                                  i = i + param.length;
                                                } else {
                                                  // No exact match found, use greedy matching until space or @
                                                  let paramEnd = i + 1;
                                                  while (paramEnd < fixedValue.length) {
                                                    if (fixedValue[paramEnd] === '@') {
                                                      break;
                                                    }
                                                    if (/\s/.test(fixedValue[paramEnd])) {
                                                      // Found space - parameter ends here (space is separator)
                                                      break;
                                                    }
                                                    paramEnd++;
                                                  }
                                                  const param = fixedValue.substring(i, paramEnd);
                                                  if (param) {
                                                    parts.push({ text: param, isParam: true });
                                                  }
                                                  i = paramEnd;
                                                }
                                              } else {
                                                currentText += fixedValue[i];
                                                i++;
                                              }
                                            }
                                            
                                            if (currentText) {
                                              parts.push({ text: currentText, isParam: false });
                                            }
                                            
                                            return parts.map((part, idx) => 
                                              part.isParam ? (
                                                <span key={idx} className="parameter-preview">
                                                  {part.text}
                                                </span>
                                              ) : (
                                                <span key={idx} className="preview-text">{part.text}</span>
                                              )
                                            );
                                          })()}
                                        </div>
                                      )}
                                    </div>
                                    {parameterAutocomplete.open && parameterAutocomplete.targetColumnKey === columnKey && (
                                      <div className="parameter-autocomplete-popup">
                                        <div className="parameter-autocomplete-tabs">
                                          <button
                                            type="button"
                                            className={`parameter-autocomplete-tab ${parameterAutocomplete.source === 'soteria' ? 'active' : ''}`}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => setParameterAutocomplete((prev) => ({ ...prev, source: 'soteria' }))}
                                          >
                                            Soteria Fields
                                          </button>
                                          <button
                                            type="button"
                                            className={`parameter-autocomplete-tab ${parameterAutocomplete.source === 'original' ? 'active' : ''}`}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => setParameterAutocomplete((prev) => ({ ...prev, source: 'original' }))}
                                          >
                                            Original Fields
                                          </button>
                                        </div>
                                        <div className="parameter-autocomplete-header">
                                          {parameterAutocomplete.source === 'soteria' ? 'Select Soteria Field' : 'Select Original Field'}
                                        </div>
                                        <div className="parameter-autocomplete-list">
                                          {(parameterAutocomplete.source === 'soteria' ? getFilteredSoteriaColumns : getFilteredOriginalColumns).length > 0 ? (
                                            (parameterAutocomplete.source === 'soteria' ? getFilteredSoteriaColumns : getFilteredOriginalColumns).map((field) => (
                                              <button
                                                key={field}
                                                className="parameter-autocomplete-option"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  handleSelectParameter(field, parameterAutocomplete.source);
                                                }}
                                                type="button"
                                              >
                                                {parameterAutocomplete.source === 'original' ? `@${ORIGINAL_PARAM_PREFIX}` : '@'}
                                                {field}
                                              </button>
                                            ))
                                          ) : (
                                            <div className="parameter-autocomplete-empty">No matching fields</div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            ) : mappedHeader ? (
                              <div className={`paired-chip ${copiedMappings[columnKey] ? 'copied-chip' : ''}`}>
                                {copiedMappings[columnKey] && (
                                  <span className="copied-indicator" title={`Copied from: ${copySourceDisplay ?? ''}`}>
                                    📋
                                  </span>
                                )}
                                <button
                                  className="preview-chip-button"
                                  onClick={(e) => handlePreviewColumn(mappedHeader, e)}
                                  title="Preview column values"
                                  aria-label="Preview column values"
                                  type="button"
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                    <circle cx="12" cy="12" r="3"></circle>
                                  </svg>
                                </button>
                                <span className="chip-text" title={mappedHeader}>
                                  {mappedHeader.length > 25
                                    ? `${mappedHeader.slice(0, 25)}…`
                                    : mappedHeader}
                                </span>
                                <button
                                  className="remove-chip-button"
                                  onClick={() => {
                                    handleRemoveMapping(columnKey);
                                  }}
                                  title="Remove mapping"
                                  aria-label="Remove mapping"
                                  type="button"
                                >
                                  ×
                                </button>
                              </div>
                            ) : fixedValue ? (
                              <div className="fixed-value-chip">
                                <span className="fixed-value-text">"{fixedValue}"</span>
                                <button
                                  className="remove-chip-button"
                                  onClick={() => {
                                    setFixedValues((prev) => {
                                      const newFixed = { ...prev };
                                      delete newFixed[columnKey];
                                      return newFixed;
                                    });
                                    // Clear download URL if file was already generated
                                    if (hasGeneratedOnce) {
                                      setDownloadUrl(null);
                                    }
                                  }}
                                  title="Remove fixed value"
                                  aria-label="Remove fixed value"
                                >
                                  ×
                                </button>
                              </div>
                            ) : isAutoCalculated ? (
                              <div className="auto-calculate-chip">
                                <span className="auto-calculate-text">Auto Calculate</span>
                                <button
                                  className="remove-chip-button"
                                  onClick={() => {
                                    setAutoCalculateFields((prev) => {
                                      const next = { ...prev };
                                      delete next[columnKey];
                                      return next;
                                    });
                                    setDeclinedAutoCalculateFields((prev) => ({
                                      ...prev,
                                      [columnKey]: true,
                                    }));
                                    if (hasGeneratedOnce) {
                                      setDownloadUrl(null);
                                    }
                                  }}
                                  title="Remove auto calculate"
                                  aria-label="Remove auto calculate"
                                >
                                  ×
                                </button>
                              </div>
                            ) : isBoxAi ? (
                              <div className="box-ai-chip">
                                <button
                                  className="preview-chip-button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenBoxAiPreview(columnKey);
                                  }}
                                  title="View Box AI values and source files"
                                  aria-label="View Box AI values and source files"
                                  type="button"
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                    <circle cx="12" cy="12" r="3"></circle>
                                  </svg>
                                </button>
                                <span className="box-ai-text">Box AI</span>
                                <button
                                  className="remove-chip-button"
                                  onClick={() => handleRemoveBoxAi(columnKey)}
                                  title="Remove Box AI values"
                                  aria-label="Remove Box AI values"
                                >
                                  ×
                                </button>
                              </div>
                            ) : isAutoGenerated ? (
                              <div className="auto-generate-chip">
                                <span className="auto-generate-text">Auto Generate</span>
                                <button
                                  className="remove-chip-button"
                                  onClick={() => {
                                    setAutoGenerateFields((prev) => {
                                      const next = { ...prev };
                                      delete next[columnKey];
                                      return next;
                                    });
                                    setDeclinedAutoGenerateFields((prev) => ({
                                      ...prev,
                                      [columnKey]: true,
                                    }));
                                    if (hasGeneratedOnce) {
                                      setDownloadUrl(null);
                                    }
                                  }}
                                  title="Remove auto generate"
                                  aria-label="Remove auto generate"
                                >
                                  ×
                                </button>
                              </div>
                            ) : originalIndex === DATA_ROOM_LINK_COL_INDEX && savedDataRoomLink ? (
                              <div className="data-room-link-chip">
                                <span className="data-room-link-text" title={savedDataRoomLink.boxFolderUrl}>
                                  {savedDataRoomLink.boxFolderUrl}
                                </span>
                                <span className="data-room-link-count">
                                  ({savedDataRoomLink.matches.filter(m => m.folderUrl).length} matched)
                                </span>
                                <button
                                  className="remove-chip-button"
                                  onClick={() => {
                                    setSavedDataRoomLink(null);
                                  }}
                                  title="Remove Data Room Link"
                                  aria-label="Remove Data Room Link"
                                >
                                  ×
                                </button>
                              </div>
                            ) : (
                              <div className="drop-zone-placeholder">
                                Drop column here
                              </div>
                            )}
                          </div>
                          {validationErrors.length > 0 && (
                            <div className="source-validation-errors">
                              <div className="source-validation-errors-title">
                                Imported values from "{validationErrors[0]?.uploadedHeader}" do not match {conflictFieldLabel}.
                              </div>
                              {validationErrors.slice(0, 3).map((validationError, idx) => (
                                <div key={`${validationError.rowNumber}-${idx}`} className="source-validation-error-item">
                                  {formatSourceValidationRowError(validationError)}
                                </div>
                              ))}
                              {validationErrors.length > 3 && (
                                <div className="source-validation-error-item">
                                  Additional invalid values were found in this column.
                                </div>
                              )}
                              <button
                                className="source-conflict-resolve-button"
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleResolveSourceConflict(columnKey);
                                }}
                                disabled={resolvingConflictColumnKey !== null}
                              >
                                {resolvingConflictColumnKey === columnKey ? 'Resolving...' : 'Resolve conflicts'}
                              </button>
                            </div>
                          )}
                          {automationWarning && (
                            <div className="automation-warning">
                              <div className="automation-warning-title">
                                This {automationWarning.mode === 'autoGenerate' ? 'generation' : 'calculation'} may fail for some rows because required inputs are incomplete.
                              </div>
                              {automationWarning.missingFields.map((issue) => (
                                <div key={issue} className="automation-warning-item">
                                  {issue}
                                </div>
                              ))}
                              {automationWarning.dependencyChecks
                                ?.filter((check) => check.status !== 'ok')
                                .map((check) => (
                                <div key={check.field} className="automation-warning-item automation-warning-detail">
                                  {check.field}: mapped to "{check.uploadHeader ?? 'not mapped'}" (Soteria index {check.mappedIndex}) — {check.rowsWithValue}/{check.rowsChecked} rows have values
                                </div>
                              ))}
                            </div>
                          )}
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>

                {/* Right Column - Unpaired Uploaded File Columns as Draggable Chips */}
                <div className="mapping-column right-column">
                  <div className="column-header-with-sort">
                    <h3>
                      Uploaded File Columns (
                      {unpairedHeaders.filter((header) => !emptyHeaders.has(header)).length} available
                      {emptyHeaders.size > 0
                        ? `, ${unpairedHeaders.filter((header) => emptyHeaders.has(header)).length} empty`
                        : ''}
                      )
                    </h3>
                    <button
                      className="sort-button"
                      onClick={() => setChipsSortOrder(cycleSortOrder(chipsSortOrder))}
                      title={`Sort: ${chipsSortOrder === 'original' ? 'Original' : chipsSortOrder === 'a-z' ? 'A→Z' : 'Z→A'}`}
                    >
                      {chipsSortOrder === 'original' && '⇅'}
                      {chipsSortOrder === 'a-z' && '↑'}
                      {chipsSortOrder === 'z-a' && '↓'}
                    </button>
                  </div>
                  <div className="column-list chip-bank">
                    {sortedChips.length > 0 ? (
                      sortedChips.map((header, index) => {
                        const isEmptyColumn = emptyHeaders.has(header);
                        return (
                        <div
                          key={index}
                          className={`draggable-chip ${isEmptyColumn ? 'empty-column' : ''} ${draggedHeader === header ? 'dragging' : ''}`}
                          draggable={!isEmptyColumn}
                          title={isEmptyColumn ? 'This column has no data in any row' : undefined}
                          onDragStart={(e) => handleDragStart(e, header)}
                          onDragEnd={handleDragEnd}
                        >
                          <button
                            className="preview-chip-button"
                            onClick={(e) => handlePreviewColumn(header, e)}
                            title={isEmptyColumn ? 'Preview column values (empty)' : 'Preview column values'}
                            aria-label={isEmptyColumn ? 'Preview column values (empty)' : 'Preview column values'}
                            type="button"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                              <circle cx="12" cy="12" r="3"></circle>
                            </svg>
                          </button>
                          <span className="chip-content">{header}</span>
                          {isEmptyColumn && (
                            <span className="empty-column-badge" title="No data in any row">
                              Empty
                            </span>
                          )}
                          <button
                            className="remove-from-bank-button"
                            onClick={(e) => handleRemoveFromBank(header, e)}
                            title="Remove from bank"
                            aria-label="Remove from bank"
                            type="button"
                          >
                            ×
                          </button>
                        </div>
                        );
                      })
                    ) : (
                      <div className="empty-bank-message">
                        All columns have been mapped or removed!
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      
      {/* Blocking progress indicator for ChatGPT pairing */}
      {isPairing && (
        <div className="pairing-overlay">
          <div className="pairing-spinner-container">
            <div className="pairing-spinner"></div>
            <p className="pairing-message">Matching columns with AI...</p>
            <p className="pairing-submessage">This may take a few seconds</p>
          </div>
        </div>
      )}

      {sourceConflictDialog.open && (
        <div className="source-conflict-dialog-overlay" onClick={handleCloseSourceConflictDialog}>
          <div className="source-conflict-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="source-conflict-dialog-header">
              <h3>Resolve Conflicts</h3>
              <button
                className="source-conflict-dialog-close"
                onClick={handleCloseSourceConflictDialog}
                aria-label="Close dialog"
                disabled={isResolvingSourceConflict}
              >
                ×
              </button>
            </div>
            <div className="source-conflict-dialog-content">
              {isResolvingSourceConflict ? (
                <div className="source-conflict-loading">
                  <div className="matching-spinner"></div>
                  <p>Generating best-effort translations...</p>
                </div>
              ) : sourceConflictDialog.error ? (
                <div className="match-error-section">
                  <strong>Error:</strong> {sourceConflictDialog.error}
                </div>
              ) : sourceConflictDialog.result ? (
                <>
                  <p className="source-conflict-dialog-description">
                    Suggested translations for <strong>{sourceConflictDialog.result.uploadedHeader}</strong> mapped to{' '}
                    <strong>{sourceConflictDialog.result.field}</strong>. Expected{' '}
                    {humanizeValidationExpected(sourceConflictDialog.result.expected)}.
                  </p>
                  {sourceConflictDialog.result.allowedValues.length > 0 && (
                    <div className="source-conflict-allowed-values">
                      Allowed values: {sourceConflictDialog.result.allowedValues.join(', ')}
                    </div>
                  )}
                  {sourceConflictDialog.result.suggestions.length === 0 ? (
                    <div className="source-conflict-empty">No conflicting values were found for this column.</div>
                  ) : (
                    <div className="source-conflict-suggestions-list">
                      {sourceConflictDialog.result.suggestions.map((suggestion) => (
                        <div key={suggestion.source_value} className="source-conflict-suggestion-row source-conflict-editable-row">
                          <div className="source-conflict-source-value">{suggestion.source_value}</div>
                          <div className="source-conflict-arrow">→</div>
                          <div className="source-conflict-suggested-value">
                            <span className="source-conflict-suggestion-label">Suggested</span>
                            {suggestion.suggested_value || 'No safe suggestion'}
                            <span className={`source-conflict-confidence confidence-${suggestion.confidence.toLowerCase()}`}>
                              {suggestion.confidence}
                            </span>
                          </div>
                          <div className="source-conflict-override">
                            <label>
                              Use value
                              {sourceConflictDialog.result?.allowedValues.length ? (
                                <select
                                  className={`source-conflict-override-input ${sourceConflictOverrideErrors[suggestion.source_value] ? 'has-error' : ''}`}
                                  value={sourceConflictOverrides[suggestion.source_value] || ''}
                                  onChange={(e) => {
                                    updateSourceConflictOverride(
                                      suggestion.source_value,
                                      e.target.value,
                                      sourceConflictDialog.result
                                    );
                                  }}
                                >
                                  <option value="">-- Select value --</option>
                                  {sourceConflictDialog.result.allowedValues.map((allowedValue) => (
                                    <option key={allowedValue} value={allowedValue}>
                                      {allowedValue}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  className={`source-conflict-override-input ${sourceConflictOverrideErrors[suggestion.source_value] ? 'has-error' : ''}`}
                                  type="text"
                                  value={sourceConflictOverrides[suggestion.source_value] || ''}
                                  placeholder={`Enter ${humanizeValidationExpected(sourceConflictDialog.result?.expected || 'resolved value')}`}
                                  onChange={(e) => {
                                    updateSourceConflictOverride(
                                      suggestion.source_value,
                                      e.target.value,
                                      sourceConflictDialog.result
                                    );
                                  }}
                                />
                              )}
                              {sourceConflictOverrideErrors[suggestion.source_value] && (
                                <div className="source-conflict-override-error">
                                  {sourceConflictOverrideErrors[suggestion.source_value]}
                                </div>
                              )}
                            </label>
                          </div>
                          <div className="source-conflict-reasoning">{suggestion.reasoning}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : null}
            </div>
            <div className="source-conflict-dialog-footer">
              {sourceConflictDialog.result && sourceConflictApplyableCount > 0 && (
                <button
                  className="source-conflict-dialog-apply"
                  onClick={handleApplySourceConflictResolution}
                >
                  Resolve and keep matched column
                </button>
              )}
              <button className="source-conflict-dialog-done" onClick={handleCloseSourceConflictDialog}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {resolvedSourceDialog.open && resolvedSourceDialogDetails && (
        <div className="source-conflict-dialog-overlay" onClick={handleCloseResolvedSourceDialog}>
          <div className="source-conflict-dialog source-resolved-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="source-conflict-dialog-header">
              <h3>Resolved Values</h3>
              <button
                className="source-conflict-dialog-close"
                onClick={handleCloseResolvedSourceDialog}
                aria-label="Close dialog"
              >
                ×
              </button>
            </div>
            <div className="source-conflict-dialog-content">
              <p className="source-conflict-dialog-description">
                Applied translations for{' '}
                {resolvedSourceDialogDetails.uploadedHeader ? (
                  <>
                    <strong>{resolvedSourceDialogDetails.uploadedHeader}</strong> mapped to{' '}
                  </>
                ) : null}
                <strong>{resolvedSourceDialogDetails.field}</strong>.
              </p>
              {resolvedSourceDialogDetails.entries.length === 0 ? (
                <div className="source-conflict-empty">No resolved values are currently stored for this column.</div>
              ) : (
                <div className="source-conflict-suggestions-list">
                  {resolvedSourceDialogDetails.entries.map(([sourceValue, resolvedValue]) => (
                    <div key={sourceValue} className="source-conflict-suggestion-row source-resolved-value-row">
                      <div className="source-conflict-source-value">{sourceValue}</div>
                      <div className="source-conflict-arrow">→</div>
                      <div className="source-conflict-suggested-value">{resolvedValue}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="source-conflict-dialog-footer">
              <button className="source-conflict-dialog-done" onClick={handleCloseResolvedSourceDialog}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Data Room Link Dialog */}
      {dataRoomLinkDialog.open && (
        <div className="data-room-link-dialog-overlay" onClick={handleCloseDataRoomLinkDialog}>
          <div className="data-room-link-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="data-room-link-dialog-header">
              <h3>Data Room Link - Box Folder Matching</h3>
              <button
                className="data-room-link-dialog-close"
                onClick={handleCloseDataRoomLinkDialog}
                aria-label="Close dialog"
              >
                ×
              </button>
            </div>
            <div className="data-room-link-dialog-content">
              {pendingBoxAiColumnKey && (
                <div className="data-room-pending-box-ai-note">
                  Set up the data room URL before using Box AI for{' '}
                  <strong>
                    {SOTERIA_COLUMNS[Number.parseInt(pendingBoxAiColumnKey, 10)] ?? 'this column'}
                  </strong>
                  . Run Match Test, then Save Link.
                </div>
              )}
              {pendingCompleteFromDataRoom && (
                <div className="data-room-pending-box-ai-note">
                  Set up the data room URL before completing columns from the Data Room.
                  Run Match Test, then Save Link.
                </div>
              )}
              <div className="data-room-link-input-section">
                <label htmlFor="box-folder-url">Box Folder URL:</label>
                <input
                  id="box-folder-url"
                  type="text"
                  className="data-room-link-url-input"
                  placeholder="https://yourdomain.box.com/folder/123456789"
                  value={boxFolderUrl}
                  onChange={(e) => setBoxFolderUrl(e.target.value)}
                  disabled={isMatching}
                />
                <button
                  className="match-test-button"
                  onClick={handleMatchTest}
                  disabled={!file || !boxFolderUrl.trim() || isMatching}
                >
                  {isMatching ? 'Matching...' : 'Match Test'}
                </button>
              </div>

              {isMatching && (
                <div className="matching-progress">
                  <div className="matching-spinner"></div>
                  <p>Matching addresses to Box folders...</p>
                </div>
              )}

              {matchError && (
                <div className="match-error-section">
                  <strong>Error:</strong> {matchError}
                </div>
              )}

              {matchResults && (
                <div className="match-results-section">
                  <div className="match-results-summary">
                    <strong>
                      {matchResults.matchedCount} of {matchResults.totalAddresses} properties matched
                    </strong>
                  </div>
                  {matchResults.matchedCount > 0 && (
                    <div className="match-results-note">
                      Matches with similarity &gt;30% will be used when generating the Data Tape.
                    </div>
                  )}
                </div>
              )}

              {savedDataRoomLink && (
                <div className="saved-link-indicator">
                  ✓ Link saved: {savedDataRoomLink.boxFolderUrl}
                </div>
              )}
            </div>
            <div className="data-room-link-dialog-footer">
              <button
                className="data-room-link-save-button"
                onClick={handleSaveDataRoomLink}
                disabled={!boxFolderUrl.trim() || !matchResults || matchResults.matchedCount === 0}
              >
                Save Link
              </button>
              <button
                className="data-room-link-cancel-button"
                onClick={handleCloseDataRoomLinkDialog}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Box AI loading overlay */}
      {isBoxAiLoading && (
        <div className="box-ai-loading-overlay">
          <div className="box-ai-loading-panel">
            <div className="matching-spinner"></div>
            <p>Querying Box AI for property documents…</p>
            <p className="box-ai-loading-subtext">This may take a minute per property.</p>
          </div>
        </div>
      )}

      {/* Box AI confirmation dialog */}
      {boxAiDialog.open && (
        <div className="box-ai-dialog-overlay" onClick={handleCloseBoxAiDialog}>
          <div className="box-ai-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="box-ai-dialog-header">
              <h3>Box AI — {boxAiDialog.columnName}</h3>
              <button
                className="box-ai-dialog-close"
                onClick={handleCloseBoxAiDialog}
                aria-label="Close dialog"
                type="button"
              >
                ×
              </button>
            </div>
            <div className="box-ai-dialog-content">
              {boxAiDialog.error ? (
                <div className="box-ai-dialog-error">{boxAiDialog.error}</div>
              ) : boxAiDialog.results.length === 0 ? (
                <div className="box-ai-dialog-empty">No results returned from Box AI.</div>
              ) : (
                <div className="box-ai-results-table-wrap">
                  <table className="box-ai-results-table">
                    <thead>
                      <tr>
                        <th>Address</th>
                        <th>Value</th>
                        <th>Source file</th>
                      </tr>
                    </thead>
                    <tbody>
                      {boxAiDialog.results.map((result) => {
                        const hasValue = result.value !== null && result.value.trim() !== '';
                        return (
                        <tr key={result.address}>
                          <td>{result.address}</td>
                          <td>
                            {hasValue ? (
                              <span className="box-ai-result-value">{result.value}</span>
                            ) : (
                              <span className="box-ai-result-missing">
                                {result.error || 'Not found'}
                              </span>
                            )}
                          </td>
                          <td>
                            {hasValue && result.fileUrl ? (
                              <a
                                href={result.fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="box-ai-file-link"
                              >
                                {result.fileName || 'View file'}
                              </a>
                            ) : (
                              <span className="box-ai-result-missing">—</span>
                            )}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="box-ai-dialog-footer">
              <button
                className="box-ai-confirm-button"
                type="button"
                onClick={handleConfirmBoxAi}
                disabled={
                  boxAiDialog.results.filter((r) => r.value !== null && r.value.trim() !== '').length === 0
                }
              >
                Use these values
              </button>
              <button
                className="box-ai-cancel-button"
                type="button"
                onClick={handleCloseBoxAiDialog}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Box AI — view applied values + source files (read-only) */}
      {boxAiPreviewDialog.open && (
        <div className="box-ai-dialog-overlay" onClick={handleCloseBoxAiPreview}>
          <div className="box-ai-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="box-ai-dialog-header">
              <h3>Box AI — {boxAiPreviewDialog.columnName}</h3>
              <button
                className="box-ai-dialog-close"
                onClick={handleCloseBoxAiPreview}
                aria-label="Close dialog"
                type="button"
              >
                ×
              </button>
            </div>
            <div className="box-ai-dialog-content">
              {boxAiPreviewRows.length === 0 ? (
                <div className="box-ai-dialog-empty">No Box AI values are stored for this column.</div>
              ) : (
                <div className="box-ai-results-table-wrap">
                  <table className="box-ai-results-table">
                    <thead>
                      <tr>
                        <th>Address</th>
                        <th>Value</th>
                        <th>Source file</th>
                      </tr>
                    </thead>
                    <tbody>
                      {boxAiPreviewRows.map((row) => {
                        const hasValue = row.value !== null && row.value.trim() !== '';
                        return (
                          <tr key={row.address}>
                            <td>{row.address}</td>
                            <td>
                              {hasValue ? (
                                <span className="box-ai-result-value">{row.value}</span>
                              ) : (
                                <span className="box-ai-result-missing">
                                  {row.error || 'Not used'}
                                </span>
                              )}
                            </td>
                            <td>
                              {hasValue && row.fileUrl ? (
                                <a
                                  href={row.fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="box-ai-file-link"
                                >
                                  {row.fileName || 'View file'}
                                </a>
                              ) : (
                                <span className="box-ai-result-missing">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="box-ai-dialog-footer">
              <button
                className="box-ai-cancel-button"
                type="button"
                onClick={handleCloseBoxAiPreview}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Complete From Data Room — confirmation */}
      {completeConfirmOpen && (
        <div className="box-ai-dialog-overlay" onClick={() => setCompleteConfirmOpen(false)}>
          <div className="complete-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="box-ai-dialog-header">
              <h3>Complete From Data Room</h3>
              <button
                className="box-ai-dialog-close"
                onClick={() => setCompleteConfirmOpen(false)}
                aria-label="Close dialog"
                type="button"
              >
                ×
              </button>
            </div>
            <div className="box-ai-dialog-content">
              <p>
                Are you sure you want to try to complete all the unpopulated columns from the
                Data Room using Box AI?
              </p>
              <p className="complete-confirm-subtext">
                {getUnpopulatedCompletableIndices().length} unpopulated column
                {getUnpopulatedCompletableIndices().length === 1 ? '' : 's'} will be queried across
                all property addresses. Already populated columns and Project Identifier, Team, and
                Loan Identification are skipped.
              </p>
            </div>
            <div className="box-ai-dialog-footer">
              <button
                className="box-ai-confirm-button"
                type="button"
                onClick={handleConfirmCompleteFromDataRoom}
                disabled={getUnpopulatedCompletableIndices().length === 0}
              >
                Yes, complete from Data Room
              </button>
              <button
                className="box-ai-cancel-button"
                type="button"
                onClick={() => setCompleteConfirmOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Complete From Data Room — loading overlay */}
      {isCompleteLoading && (
        <div className="box-ai-loading-overlay">
          <div className="box-ai-loading-panel">
            <div className="matching-spinner"></div>
            <p>Completing columns from the Data Room with Box AI…</p>
            <p className="box-ai-loading-subtext">
              Running every property in parallel. This may take a few minutes.
            </p>
          </div>
        </div>
      )}

      {/* Complete From Data Room — results with vertical tabs */}
      {completeDialog.open && (
        <div className="box-ai-dialog-overlay" onClick={handleCloseCompleteDialog}>
          <div className="complete-results-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="box-ai-dialog-header">
              <h3>Box AI — Complete From Data Room</h3>
              <button
                className="box-ai-dialog-close"
                onClick={handleCloseCompleteDialog}
                aria-label="Close dialog"
                type="button"
              >
                ×
              </button>
            </div>
            {completeDialog.error ? (
              <div className="box-ai-dialog-content">
                <div className="box-ai-dialog-error">{completeDialog.error}</div>
              </div>
            ) : completeDialog.columns.length === 0 ? (
              <div className="box-ai-dialog-content">
                <div className="box-ai-dialog-empty">No results returned from Box AI.</div>
              </div>
            ) : (
              <div className="complete-results-body">
                <div className="complete-results-tabs" role="tablist">
                  {completeDialog.columns.map((column) => {
                    const foundCount = column.results.filter(
                      (r) => r.value !== null && r.value.trim() !== ''
                    ).length;
                    const isActive = completeDialog.activeColumnIndex === column.columnIndex;
                    const isExcluded = completeDialog.excludedColumnKeys.includes(
                      String(column.columnIndex)
                    );
                    return (
                      <button
                        key={column.columnIndex}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        className={`complete-results-tab ${isActive ? 'active' : ''} ${
                          isExcluded ? 'excluded' : ''
                        }`}
                        onClick={() =>
                          setCompleteDialog((prev) => ({
                            ...prev,
                            activeColumnIndex: column.columnIndex,
                          }))
                        }
                      >
                        <span className="complete-results-tab-name">{column.columnName}</span>
                        {isExcluded ? (
                          <span className="complete-results-tab-skip">Skipped</span>
                        ) : (
                          <span
                            className={`complete-results-tab-count ${
                              foundCount > 0 ? 'has-values' : ''
                            }`}
                          >
                            {foundCount}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                <div className="complete-results-panel">
                  {(() => {
                    const activeColumn =
                      completeDialog.columns.find(
                        (c) => c.columnIndex === completeDialog.activeColumnIndex
                      ) ?? completeDialog.columns[0];
                    if (!activeColumn) return null;
                    const activeKey = String(activeColumn.columnIndex);
                    const isExcluded = completeDialog.excludedColumnKeys.includes(activeKey);
                    return (
                      <div className="box-ai-results-table-wrap">
                        <label className="complete-results-use-toggle">
                          <input
                            type="checkbox"
                            checked={!isExcluded}
                            onChange={(e) => {
                              const include = e.target.checked;
                              setCompleteDialog((prev) => {
                                const set = new Set(prev.excludedColumnKeys);
                                if (include) {
                                  set.delete(activeKey);
                                } else {
                                  set.add(activeKey);
                                }
                                return { ...prev, excludedColumnKeys: Array.from(set) };
                              });
                            }}
                          />
                          <span>Use these values for "{activeColumn.columnName}"</span>
                        </label>
                        <table className="box-ai-results-table">
                          <thead>
                            <tr>
                              <th>Address</th>
                              <th>Value</th>
                              <th>Source file</th>
                            </tr>
                          </thead>
                          <tbody>
                            {activeColumn.results.map((result) => {
                              const hasValue =
                                result.value !== null && result.value.trim() !== '';
                              return (
                              <tr key={result.address}>
                                <td>{result.address}</td>
                                <td>
                                  {hasValue ? (
                                    <span className="box-ai-result-value">{result.value}</span>
                                  ) : (
                                    <span className="box-ai-result-missing">
                                      {result.error || 'Not found'}
                                    </span>
                                  )}
                                </td>
                                <td>
                                  {hasValue && result.fileUrl ? (
                                    <a
                                      href={result.fileUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="box-ai-file-link"
                                    >
                                      {result.fileName || 'View file'}
                                    </a>
                                  ) : (
                                    <span className="box-ai-result-missing">—</span>
                                  )}
                                </td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}
            <div className="box-ai-dialog-footer">
              <button
                className="box-ai-confirm-button"
                type="button"
                onClick={handleConfirmCompleteDialog}
                disabled={
                  completeDialog.columns.filter(
                    (c) =>
                      !completeDialog.excludedColumnKeys.includes(String(c.columnIndex)) &&
                      c.results.some((r) => r.value !== null && r.value.trim() !== '')
                  ).length === 0
                }
              >
                Use these values
              </button>
              <button
                className="box-ai-cancel-button"
                type="button"
                onClick={handleCloseCompleteDialog}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Automated "Upload & Generate Datatape" progress dialog */}
      {autoBuild.phase !== 'idle' && (
        <div className="auto-build-overlay">
          <div className="auto-build-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="auto-build-header">
              <h3>
                {isBoxDataroomMode
                  ? 'Generate Datatape From Box Data Room (Single Project)'
                  : 'Upload & Generate Datatape'}
              </h3>
              {(autoBuild.phase === 'done' || autoBuild.phase === 'error') && (
                <button
                  className="auto-build-close"
                  type="button"
                  onClick={cancelAutoBuild}
                  aria-label="Close"
                >
                  ×
                </button>
              )}
            </div>

            {autoBuild.phase === 'collect-url' ? (
              <div className="auto-build-body">
                <p className="auto-build-intro">
                  This will automatically map your file, resolve conflicts, auto-fill and
                  auto-calculate fields, collect remaining data from the Data Room with Box AI,
                  then generate and download the Soteria Data Tape.
                </p>
                <label className="auto-build-url-label" htmlFor="auto-build-url">
                  Data Room URL (Box folder link)
                </label>
                <input
                  id="auto-build-url"
                  className="auto-build-url-input"
                  type="text"
                  placeholder="https://app.box.com/folder/..."
                  value={autoBuildUrlInput}
                  onChange={(e) => setAutoBuildUrlInput(e.target.value)}
                  autoFocus
                />
                <p className="auto-build-hint">
                  The Data Room URL is required so Box AI can collect values for the remaining
                  columns.
                </p>
                <div className="auto-build-footer">
                  <button
                    className="auto-build-start"
                    type="button"
                    onClick={confirmAutoBuildUrl}
                    disabled={!autoBuildUrlInput.trim()}
                  >
                    Start
                  </button>
                  <button className="auto-build-cancel" type="button" onClick={cancelAutoBuild}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="auto-build-body">
                {(() => {
                  const total = autoBuild.steps.length || 1;
                  const completed = autoBuild.steps.filter((s) => s.status === 'done').length;
                  const pct =
                    autoBuild.phase === 'done'
                      ? 100
                      : Math.round((completed / total) * 100);
                  return (
                    <div className="auto-build-progress">
                      <div className="auto-build-progress-track">
                        <div
                          className={`auto-build-progress-fill ${
                            autoBuild.phase === 'error' ? 'error' : ''
                          }`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <div className="auto-build-progress-label">{pct}%</div>
                    </div>
                  );
                })()}

                <ol className="auto-build-steps">
                  {autoBuild.steps.map((step) => (
                    <li key={step.id} className={`auto-build-step status-${step.status}`}>
                      <span className="auto-build-step-icon" aria-hidden="true">
                        {step.status === 'done'
                          ? '✓'
                          : step.status === 'error'
                            ? '!'
                            : step.status === 'active'
                              ? ''
                              : ''}
                        {step.status === 'active' && (
                          <span className="auto-build-step-spinner" />
                        )}
                      </span>
                      <span className="auto-build-step-text">
                        <span className="auto-build-step-label">{step.label}</span>
                        {step.detail && (
                          <span className="auto-build-step-detail">{step.detail}</span>
                        )}
                      </span>
                    </li>
                  ))}
                </ol>

                {autoBuild.phase === 'resolve-conflicts' && (
                  <div className="auto-build-conflicts">
                    <h4>Resolve remaining conflicts</h4>
                    <p className="auto-build-conflicts-intro">
                      These values couldn&apos;t be auto-resolved with confidence. Choose a value
                      for each, then continue.
                    </p>
                    {autoBuildConflicts.map((conflict) => {
                      const columnKey = String(conflict.columnIndex);
                      return (
                        <div key={columnKey} className="auto-build-conflict-card">
                          <div className="auto-build-conflict-title">
                            {conflict.field}
                            <span className="auto-build-conflict-expected">
                              expects {humanizeValidationExpected(conflict.expected)}
                            </span>
                          </div>
                          {conflict.suggestions.map((suggestion) => {
                            const inputValue =
                              autoBuildConflictInputs[columnKey]?.[suggestion.source_value] ?? '';
                            const inputError =
                              autoBuildConflictErrors[columnKey]?.[suggestion.source_value];
                            return (
                              <div
                                key={suggestion.source_value}
                                className="auto-build-conflict-row"
                              >
                                <div className="auto-build-conflict-source">
                                  {suggestion.source_value}
                                  <span
                                    className={`source-conflict-confidence confidence-${suggestion.confidence.toLowerCase()}`}
                                  >
                                    {suggestion.confidence}
                                  </span>
                                </div>
                                <div className="auto-build-conflict-arrow">→</div>
                                <div className="auto-build-conflict-input-wrap">
                                  {conflict.allowedValues.length > 0 ? (
                                    <select
                                      className={`source-conflict-override-input ${
                                        inputError ? 'has-error' : ''
                                      }`}
                                      value={inputValue}
                                      onChange={(e) =>
                                        updateAutoBuildConflictInput(
                                          columnKey,
                                          suggestion.source_value,
                                          e.target.value,
                                          conflict
                                        )
                                      }
                                    >
                                      <option value="">-- Select value --</option>
                                      {conflict.allowedValues.map((allowedValue) => (
                                        <option key={allowedValue} value={allowedValue}>
                                          {allowedValue}
                                        </option>
                                      ))}
                                    </select>
                                  ) : (
                                    <input
                                      className={`source-conflict-override-input ${
                                        inputError ? 'has-error' : ''
                                      }`}
                                      type="text"
                                      value={inputValue}
                                      placeholder={`Enter ${humanizeValidationExpected(conflict.expected)}`}
                                      onChange={(e) =>
                                        updateAutoBuildConflictInput(
                                          columnKey,
                                          suggestion.source_value,
                                          e.target.value,
                                          conflict
                                        )
                                      }
                                    />
                                  )}
                                  {inputError && (
                                    <div className="source-conflict-override-error">
                                      {inputError}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                    <div className="auto-build-footer">
                      <button
                        className="auto-build-start"
                        type="button"
                        onClick={handleAutoBuildConflictsContinue}
                      >
                        Continue
                      </button>
                      <button
                        className="auto-build-cancel"
                        type="button"
                        onClick={cancelAutoBuild}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {autoBuild.phase === 'error' && (
                  <div className="auto-build-error">{autoBuild.error}</div>
                )}

                {autoBuild.phase === 'done' && (
                  <div className="auto-build-done">
                    <div className="auto-build-done-message">
                      ✓ Your Soteria Data Tape was generated and downloaded.
                    </div>
                    <div className="auto-build-footer">
                      {autoBuild.downloadUrl && (
                        <a
                          className="auto-build-download"
                          href={autoBuild.downloadUrl}
                          download="soteria-data-tape.xlsx"
                        >
                          📥 Download again
                        </a>
                      )}
                      <button
                        className="auto-build-cancel"
                        type="button"
                        onClick={cancelAutoBuild}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}

                {autoBuild.phase === 'running' && (
                  <div className="auto-build-footer auto-build-footer-secondary">
                    <button
                      className="auto-build-cancel"
                      type="button"
                      onClick={cancelAutoBuild}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Copy Dialog */}
      {copyDialog.open && (
        <div className="copy-dialog-overlay" onClick={() => setCopyDialog({ open: false, targetColumnKey: null })}>
          <div className="copy-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="copy-dialog-header">
              <h3>Copy from Paired Column</h3>
              <button
                className="copy-dialog-close"
                onClick={() => setCopyDialog({ open: false, targetColumnKey: null })}
                aria-label="Close dialog"
              >
                ×
              </button>
            </div>
            <div className="copy-dialog-content">
              {getPairedSoteriaColumns().length === 0 ? (
                <div className="copy-empty">No paired columns available to copy from.</div>
              ) : (
                <div className="copy-options-list">
                  {getPairedSoteriaColumns().map(({ columnKey, uploadedHeader, displayLabel }) => (
                    <button
                      key={columnKey}
                      className="copy-option-button"
                      onClick={() => handleSelectCopiedMapping(columnKey)}
                    >
                      <div className="copy-option-soteria">{displayLabel}</div>
                      <div className="copy-option-header">→ {uploadedHeader}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Preview Dialog */}
      {previewDialog.open && (
        <div className="preview-dialog-overlay" onClick={handleClosePreview}>
          <div className="preview-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="preview-dialog-header">
              <h3>Column Values: {previewDialog.column}</h3>
              <button
                className="preview-dialog-close"
                onClick={handleClosePreview}
                aria-label="Close dialog"
              >
                ×
              </button>
            </div>
            <div className="preview-dialog-content">
              {isLoadingPreview ? (
                <div className="preview-loading">Loading values...</div>
              ) : previewDialog.values.length === 0 ? (
                <div className="preview-empty">No values found for this column.</div>
              ) : (
                <div className="preview-values-list">
                  {previewDialog.values.map((value, index) => (
                    <div key={index} className="preview-value-row">
                      {value}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="preview-dialog-footer">
              <span className="preview-count">
                {previewDialog.values.length} {previewDialog.values.length === 1 ? 'value' : 'values'}
              </span>
              <button className="preview-dialog-close-button" onClick={handleClosePreview}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ContainerExcelBuilder;
